/**
 * Shuttle Checkout Manager
 * Main checkout page controller extending ShuttleBookingManager
 * 
 * @package A2A_Theme
 */

class ShuttleCheckoutManager extends ShuttleBookingManager {
    constructor() {
        // Pass skipAutoInit flag to parent constructor
        super(true);
        
        this.checkoutData = this.initializeCheckoutData();
        this.formValidator = null;
        this.summaryCalculator = null;
        this.apiManager = null;
        this.shouldShowBusStops = true; // Will be set to false if no bus stops available
        this.busStopDirection = null; // Will be set to 'outbound' or 'return' when bus stops are loaded
        this.originalProfileData = null; // Stores original profile data for comparison
        
        // Load translatable strings from DOM (Weglot-compatible)
        this.translatableStrings = this.loadTranslatableStrings();
    }
    
    /**
     * Load translatable strings from hidden DOM elements
     * This allows Weglot to translate them
     * We wait for Weglot to be ready before loading
     */
    loadTranslatableStrings() {
        const container = document.getElementById('shuttle-translatable-strings');
        if (!container) {
            console.warn('Translatable strings container not found');
            return {};
        }
        
        const strings = {};
        const spans = container.querySelectorAll('span[data-key]');
        spans.forEach(span => {
            const key = span.getAttribute('data-key');
            const text = span.textContent;
            strings[key] = text;
        });
        
        return strings;
    }
    
    /**
     * Get translated string by key
     * Uses smart templates with numeric placeholders
     * @param {string} key - String key
     * @param {array} replacements - Optional array of replacements for {0}, {1}, etc.
     * @returns {string} Translated string
     */
    getTranslatedString(key, replacements = []) {
        const template = this.translatableStrings[key];
        if (!template) {
            console.warn(`Translation key not found: ${key}`);
            return '';
        }
        
        // Replace numeric placeholders {0}, {1}, etc.
        let result = template;
        replacements.forEach((value, index) => {
            result = result.replace(`{${index}}`, value);
        });
        
        return result;
    }
    
    
    initializeCheckoutData() {
        return {
            leadPassenger: {
                email: '',
                firstName: '',
                lastName: '',
                phone: '',
                alternatePhone: '',
                countryCode: '+44',
                alternateCountryCode: '+44'
            },
            busStop: null,
            fareCondition: 'skier',
            luggage: {
                additional_bag: 0,
                ski_bag: 0
            },
            childSeats: {
                regular: 0,
                booster: 0
            },
            additionalInfo: '',
            userEditedProfileData: false, // Tracks if logged-in user edited prefilled data
            pricing: {
                baseFare: 0,
                fareUpgrade: 0,
                luggage: 0,
                childSeats: 0,
                total: 0,
                currency: this.bookingData?.currency || 'EUR'
            }
        };
    }
    
    async init() {
        try {
            // Initialize parent class first
            await super.init();
            
            // Validate that we have booking data from previous steps
            if (!this.validateCheckoutData()) {
                this.redirectToStep1();
                return;
            }
            
            // Initialize checkout-specific components
            this.formValidator = new CheckoutFormValidator();
            this.formValidator.init();
            
            // Load required data
            await this.loadCheckoutData();
            
            // Initialize currency from localStorage and update booking data
            await this.initializeCurrency();
            
            // Immediately correct PHP defaults with actual localStorage data
            this.correctPHPDefaults();
            
            // Initialize form components
            this.initializeForm();
            this.initializePhoneInputSafely();
            this.initializeBusStops();
            this.initializeFareConditions();
            this.initializeLuggage();
            this.initializeChildSeats();
            
            // Initialize summary
            this.updateBookingSummary();
            
            // Set up event listeners
            this.setupEventListeners();
            this.setupCurrencyListener();
            
            // Initialize progress centering for mobile
            this.initializeProgressCentering();
            
            // Handle browser back button - remove loading overlay
            this.setupBackButtonHandler();
            
            // Track GA UA Enhanced Ecommerce: Checkout Step 1 (STEP 3)
            // Fires on page load, before order is created
            this.trackCheckoutPageLoad();
            
        } catch (error) {
            console.error('Failed to initialize checkout:', error);
            this.showError(this.getTranslatedString('checkoutLoadError'));
        }
    }
    
    /**
     * Initialize progress centering for mobile devices
     * Centers the "Passenger information" step on screens ≤768px
     */
    initializeProgressCentering() {
        // Only apply on mobile/tablet screens
        if (window.innerWidth <= 768) {
            this.centerPassengerStep();
        }
        
        // Re-center on window resize
        jQuery(window).on('resize.progressCentering', () => {
            if (window.innerWidth <= 768) {
                this.centerPassengerStep();
            } else {
                this.resetProgressCentering();
            }
        });
    }
    
    /**
     * Center the passenger information step on mobile
     */
    centerPassengerStep() {
        const progressSteps = jQuery('.shuttle-progress__steps');
        const passengerStep = jQuery('.shuttle-progress__step[data-step="passenger"]');
        
        if (progressSteps.length && passengerStep.length) {
            // Add centering class for CSS animation
            progressSteps.addClass('shuttle-progress__steps--center-passenger');
            
            // Calculate dynamic centering if needed
            const containerWidth = progressSteps.parent().width();
            const passengerStepOffset = passengerStep.position().left;
            const passengerStepWidth = passengerStep.outerWidth();
            
            if (containerWidth && passengerStepOffset !== undefined) {
                const centerPosition = containerWidth / 2;
                const passengerStepCenter = passengerStepOffset + (passengerStepWidth / 2);
                const translateX = centerPosition - passengerStepCenter;
                
                // Apply the calculated transform
                progressSteps.css('transform', `translateX(${translateX}px)`);
            }
        }
    }
    
    /**
     * Reset progress centering to default position
     */
    resetProgressCentering() {
        const progressSteps = jQuery('.shuttle-progress__steps');
        progressSteps.removeClass('shuttle-progress__steps--center-passenger');
        progressSteps.css('transform', 'translateX(0)');
    }
    
    /**
     * Setup handler to remove loading overlay when user navigates back
     */
    setupBackButtonHandler() {
        window.addEventListener('pageshow', (event) => {
            // Detect navigation via browser back/forward (cached or history)
            if (event.persisted || (performance.getEntriesByType('navigation')[0]?.type === 'back_forward')) {
                // Remove loading overlay if it exists
                jQuery('.shuttle-checkout__loading-overlay').remove();
                
                // Re-enable the continue button
                const button = jQuery('#continue-to-payment');
                button.prop('disabled', false);
                button.html('Continue to Payment');
            }
        });
    }
    
    /**
     * Initialize currency from localStorage and update booking data
     */
    async initializeCurrency() {
        try {
            // Get current currency from localStorage (set by header currency switcher)
            const selectedCurrency = localStorage.getItem('selectedCurrency') || 'EUR';
            const selectedCurrencySymbol = localStorage.getItem('selectedCurrencySymbol') || '€';
            
            // Update currency manager if different from current
            if (this.currencyManager.currentCurrency !== selectedCurrency) {
                await this.currencyManager.updateCurrency(selectedCurrency, selectedCurrencySymbol);
            }
            
            // Update booking data currency
            this.bookingData.currency = selectedCurrency;
            this.saveBookingData(this.bookingData);
            
            
        } catch (error) {
            console.error('Failed to initialize currency:', error);
            // Continue with EUR as fallback
        }
    }
    
    validateCheckoutData() {
        const data = this.bookingData;
        
        // Check if we have required booking data
        if (!data.selectedRoutes || !data.selectedRoutes.outbound) {
            return false;
        }
        
        // Check if we have passenger data
        if (!data.passengers || (!data.passengers.adults && !data.passengers.children)) {
            return false;
        }
        
        return true;
    }
    
    requiresBusStops() {
        // Bus stops are only required when the destination is a resort
        return this.getResortIdFromBookingData() !== null;
    }
    
    
    redirectToStep1() {
        // Use the landing page URL from admin settings
        const landingPageUrl = window.shuttleLandingPageUrl || '/';
        // Use buildLanguageAwareUrl to respect Weglot language prefix
        window.location.href = this.buildLanguageAwareUrl(landingPageUrl);
    }
    
    async loadCheckoutData() {
        // Load fare pricing from existing route data
        await this.loadFarePricing();
        
        // Load extras data (placeholder for now)
        await this.loadExtrasData();
        
        // Load bus stops from cache
        await this.loadBusStops();
        
        // Initialize default pricePreferences if not set
        this.initializePricePreferences();
        
        // Sync checkout data with booking data
        this.syncCheckoutDataWithBookingData();
    }
    
    initializePricePreferences() {
        // Ensure pricePreferences exists with default values
        if (!this.bookingData.pricePreferences) {
            this.bookingData.pricePreferences = {};
        }
        
        // Set default tier to 'skier' if not already set
        if (!this.bookingData.pricePreferences.tier) {
            this.bookingData.pricePreferences.tier = 'skier';
            
            // Save to localStorage
            localStorage.setItem('shuttle_booking_data', JSON.stringify(this.bookingData));
        }
    }
    
    syncCheckoutDataWithBookingData() {
        // Sync fare condition from booking data
        if (this.bookingData.pricePreferences?.tier) {
            this.checkoutData.fareCondition = this.bookingData.pricePreferences.tier;
        }
    }
    
    async loadFarePricing() {
        // Get pricing from selected routes
        const selectedRoutes = this.bookingData.selectedRoutes;
        if (selectedRoutes.outbound && selectedRoutes.outbound.prices) {
            this.farePricing = selectedRoutes.outbound.prices;
        }
    }
    
    // User data detection methods removed - will be reimplemented with different approach later
    
    /**
     * Placeholder for future user data loading implementation
     */
    async loadUserData() {
        // TODO: Implement user data loading with new approach
    }

    async loadExtrasData() {
        try {
            // Load extras data from cache
            const cacheUrl = `${this.uploadsUrl}shuttle-cache/${this.version}/extras.json`;
            const response = await fetch(cacheUrl);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const extrasData = await response.json();
            
            // Map API data to expected format
            this.extrasData = {
                luggage: extrasData.luggage || {
                    code: 'luggage',
                    description: 'Additional luggage item',
                    price: 20,
                    currency: 'EUR'
                },
                ski: extrasData.ski || {
                    code: 'ski',
                    description: 'Ski/snowboard bag',
                    price: 20,
                    currency: 'EUR'
                },
                child_seat: extrasData.child_seat || {
                    code: 'child_seat',
                    description: 'Child seat',
                    price: 2,
                    currency: 'EUR'
                },
                booster_seat: extrasData.booster_seat || {
                    code: 'booster_seat',
                    description: 'Booster seat',
                    price: 2,
                    currency: 'EUR'
                }
            };
            
        } catch (error) {
            console.error('Failed to load extras data:', error);
            
            // Fallback to default data
            this.extrasData = {
                luggage: {
                    code: 'luggage',
                    description: 'Additional luggage item',
                    price: 20,
                    currency: 'EUR'
                },
                ski: {
                    code: 'ski',
                    description: 'Ski/snowboard bag',
                    price: 20,
                    currency: 'EUR'
                },
                child_seat: {
                    code: 'child_seat',
                    description: 'Child seat',
                    price: 2,
                    currency: 'EUR'
                },
                booster_seat: {
                    code: 'booster_seat',
                    description: 'Booster seat',
                    price: 2,
                    currency: 'EUR'
                }
            };
        }
    }
    
    async loadBusStops() {
        try {
            // Get resort ID and direction from booking data
            const resortInfo = this.getResortIdFromBookingData();
            
            if (!resortInfo) {
                this.busStops = [];
                this.shouldShowBusStops = false;
                this.busStopDirection = null;
                return;
            }
            
            // Store the direction for later use in description text
            this.busStopDirection = resortInfo.direction;
            
            // Load cached bus stops for the resort
            const cacheFile = `stops-by-resort/${resortInfo.resortId}.json`;
            const cacheUrl = `${this.uploadsUrl}/shuttle-cache/${this.version}/${cacheFile}`;
            
            const response = await fetch(cacheUrl);
            
            if (!response.ok) {
                this.busStops = [];
                this.shouldShowBusStops = false;
                this.busStopDirection = null;
                return;
            }
            
            const stopsData = await response.json();
            
            if (Array.isArray(stopsData) && stopsData.length > 0) {
                this.busStops = stopsData;
                this.shouldShowBusStops = true;
            } else {
                this.busStops = [];
                this.shouldShowBusStops = false;
                this.busStopDirection = null;
            }
            
        } catch (error) {
            console.error('Error loading bus stops:', error);
            this.busStops = [];
            this.shouldShowBusStops = false;
            this.busStopDirection = null;
        }
    }
    
    getResortIdFromBookingData() {
        // Get resort ID from booking data (check both directions)
        // Returns object with resortId and direction ('outbound' or 'return')
        const transferType = this.bookingData.transferType;
        const selectedRoutes = this.bookingData.selectedRoutes;
        
        // Helper function to extract ID from object
        const extractId = (obj) => {
            if (!obj) return null;
            if (typeof obj === 'string') return obj;
            if (obj.id) return obj.id;
            return null;
        };
        
        // Helper function to check if ID is a resort
        const isResort = (id) => {
            return id && id.toString().startsWith('resort-');
        };
        
        // For outbound: check both FROM and TO
        let outboundFromId = null;
        let outboundToId = null;
        
        // Get outbound FROM (origin)
        if (this.bookingData.from) {
            outboundFromId = extractId(this.bookingData.from);
        } else if (selectedRoutes?.outbound?.routeData?.from) {
            outboundFromId = extractId(selectedRoutes.outbound.routeData.from);
        } else if (selectedRoutes?.outbound?.from) {
            outboundFromId = extractId(selectedRoutes.outbound.from);
        }
        
        // Get outbound TO (destination)
        if (this.bookingData.to) {
            outboundToId = extractId(this.bookingData.to);
        } else if (selectedRoutes?.outbound?.routeData?.to) {
            outboundToId = extractId(selectedRoutes.outbound.routeData.to);
        } else if (selectedRoutes?.outbound?.to) {
            outboundToId = extractId(selectedRoutes.outbound.to);
        }
        
        // Check if outbound origin is a resort (resort -> airport)
        if (isResort(outboundFromId)) {
            return { resortId: outboundFromId, direction: 'outbound' };
        }
        
        // Check if outbound destination is a resort (airport -> resort)
        if (isResort(outboundToId)) {
            return { resortId: outboundToId, direction: 'outbound' };
        }
        
        // For return trips, check the return direction as well
        if (transferType === 'return') {
            // For return, from/to are swapped
            // Return FROM would be the outbound TO
            // Return TO would be the outbound FROM
            
            // The resort could be at either end of the return journey
            // But we already checked both ends above for outbound
            // So if we reach here, there's no resort in either direction
        }
        
        return null;
    }
    
    
    initializeForm() {
        // Initialize floating labels
        this.initializeFloatingLabels();
        
        // Initialize phone number input
        this.initializePhoneInput();
        
        // Email recognition functionality will be added later
        // this.initializeEmailRecognition();
        
        // Auto-populate form if user is logged in (future feature)
        // For now, just ensure form is ready
    }
    
    initializeFloatingLabels() {
        // Handle floating label states for all floating label fields
        const floatingFields = document.querySelectorAll('.shuttle-checkout__field--floating');
        
        floatingFields.forEach(field => {
            const input = field.querySelector('.shuttle-checkout__input');
            const label = field.querySelector('.shuttle-checkout__label--floating');
            
            if (!input || !label) return;
            
            // Check initial state
            this.updateFloatingLabel(input);
            
            // Add event listeners
            input.addEventListener('focus', () => this.updateFloatingLabel(input));
            input.addEventListener('blur', () => this.updateFloatingLabel(input));
            input.addEventListener('input', () => this.updateFloatingLabel(input));
        });
    }
    
    updateFloatingLabel(input) {
        const hasValue = input.value.trim() !== '';
        const isFocused = document.activeElement === input;
        
        // Special handling for phone inputs with intl-tel-input
        const isPhoneInput = input.classList.contains('shuttle-checkout__input--phone');
        let shouldFloat = hasValue || isFocused;
        
        if (isPhoneInput) {
            // For phone inputs, also check if there's any formatted content
            // intl-tel-input might have placeholder or formatted content
            const phoneValue = input.value.replace(/[\s\-\(\)]/g, ''); // Remove formatting
            shouldFloat = phoneValue.length > 0 || isFocused;
            
            // For phone inputs, we need to manually handle the label since DOM structure is different
            const phoneField = input.closest('.shuttle-checkout__field--phone');
            const label = phoneField ? phoneField.querySelector('.shuttle-checkout__label--floating') : null;
            
            if (label) {
                // Remove existing state classes
                label.classList.remove('shuttle-checkout__label--focused', 'shuttle-checkout__label--filled');
                
                // Add appropriate state class
                if (isFocused) {
                    label.classList.add('shuttle-checkout__label--focused');
                } else if (shouldFloat) {
                    label.classList.add('shuttle-checkout__label--filled');
                }
            }
        }
        
        if (shouldFloat) {
            input.classList.add('shuttle-checkout__input--filled');
        } else {
            input.classList.remove('shuttle-checkout__input--filled');
        }
    }
    
    initializePhoneInput() {
        if (typeof window.CustomPhoneInput === 'undefined') {
            console.error('CustomPhoneInput class not available');
            return;
        }
        
        // Initialize primary phone input
        const phoneInput = document.querySelector('#passenger-phone');
        if (phoneInput) {
            // Check if already initialized to prevent duplicates
            if (phoneInput.hasAttribute('data-custom-phone-initialized')) {
                return;
            }
            
                this.phoneInputInstance = new window.CustomPhoneInput(phoneInput, {
                    preferredCountries: ['gb', 'us', 'fr', 'de', 'it', 'es', 'ch', 'at']
                });
            
            phoneInput.setAttribute('data-custom-phone-initialized', 'true');
            
            // Handle floating label for primary phone input
            phoneInput.addEventListener('countrychange', (e) => {
                this.updateFloatingLabel(phoneInput);
                
                // Save phone data with new country code to localStorage when user manually changes country
                // Don't save during programmatic changes (e.g., when restoring from localStorage or prefilling profile data)
                if (e.detail && !e.detail.isProgrammatic) {
                    const phoneValue = jQuery(phoneInput).val().trim();
                    if (phoneValue) {
                        this.saveFormFieldToLocalStorage('phone', phoneValue);
                    }
                }
            });
            
            // Additional event listeners for phone input
            phoneInput.addEventListener('input', () => {
                this.updateFloatingLabel(phoneInput);
            });
            phoneInput.addEventListener('keyup', () => {
                this.updateFloatingLabel(phoneInput);
            });
            phoneInput.addEventListener('paste', () => {
                setTimeout(() => this.updateFloatingLabel(phoneInput), 10);
            });
        }
        
        // Initialize alternate phone input
        const alternatePhoneInput = document.querySelector('#passenger-alternate-phone');
        if (alternatePhoneInput) {
            // Check if already initialized to prevent duplicates
            if (alternatePhoneInput.hasAttribute('data-custom-phone-initialized')) {
                return;
            }
            
            // Remove required attribute since this is optional
            alternatePhoneInput.removeAttribute('required');
            
                this.alternatePhoneInputInstance = new window.CustomPhoneInput(alternatePhoneInput, {
                    preferredCountries: ['gb', 'us', 'fr', 'de', 'it', 'es', 'ch', 'at']
                });
            
            alternatePhoneInput.setAttribute('data-custom-phone-initialized', 'true');
            
            // Handle floating label for alternate phone input
            alternatePhoneInput.addEventListener('countrychange', (e) => {
                this.updateFloatingLabel(alternatePhoneInput);
                
                // Save alternate phone data with new country code when user manually changes country
                if (e.detail && !e.detail.isProgrammatic) {
                    const phoneValue = jQuery(alternatePhoneInput).val().trim();
                    if (phoneValue) {
                        this.saveFormFieldToLocalStorage('alternatePhone', phoneValue);
                    }
                }
            });
            
            // Additional event listeners for alternate phone input
            alternatePhoneInput.addEventListener('input', () => {
                this.updateFloatingLabel(alternatePhoneInput);
            });
            alternatePhoneInput.addEventListener('keyup', () => {
                this.updateFloatingLabel(alternatePhoneInput);
            });
            alternatePhoneInput.addEventListener('paste', () => {
                setTimeout(() => this.updateFloatingLabel(alternatePhoneInput), 10);
            });
        }
        
        // Initialize phone field labels on page load
        setTimeout(() => {
            if (phoneInput) this.updateFloatingLabel(phoneInput);
            if (alternatePhoneInput) this.updateFloatingLabel(alternatePhoneInput);
        }, 100);
    }
    
    initializeEmailRecognition() {
        const emailInput = document.querySelector('#passenger-email');
        const emailField = document.querySelector('.shuttle-checkout__field--email');
        const passwordField = document.querySelector('.shuttle-checkout__field--password');
        
        if (!emailInput || !emailField || !passwordField) return;
        
        // Simulate email recognition (in real implementation, this would be an API call)
        emailInput.addEventListener('blur', () => {
            const email = emailInput.value.trim();
            if (this.isValidEmail(email)) {
                // Simulate checking if email exists in system
                // For demo purposes, let's say any email with "test" shows password field
                if (email.includes('test') || email.includes('demo')) {
                    this.showPasswordField(emailField, passwordField);
                } else {
                    this.hidePasswordField(emailField, passwordField);
                }
            } else {
                this.hidePasswordField(emailField, passwordField);
            }
        });
    }
    
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    
    showPasswordField(emailField, passwordField) {
        emailField.classList.add('shuttle-checkout__field--with-password');
        passwordField.classList.add('shuttle-checkout__field--visible');
        
        // Make password field required when visible
        const passwordInput = passwordField.querySelector('input');
        if (passwordInput) {
            passwordInput.required = true;
        }
    }
    
    hidePasswordField(emailField, passwordField) {
        emailField.classList.remove('shuttle-checkout__field--with-password');
        passwordField.classList.remove('shuttle-checkout__field--visible');
        
        // Remove required attribute when hidden
        const passwordInput = passwordField.querySelector('input');
        if (passwordInput) {
            passwordInput.required = false;
            passwordInput.value = '';
            this.updateFloatingLabel(passwordInput);
        }
    }
    
    initializeForm() {
        // Basic form initialization - floating labels, etc.
        // Most form functionality is handled by the validation class
        this.setupFloatingLabels();
        this.setupPasswordToggle();
        
        // Check if user is logged in and adapt UI accordingly
        this.handleUserLoginStatus();
        
        // Listen for login status changes (e.g., after account creation or login)
        window.addEventListener('shuttleUserLoginStatusChanged', (event) => {
            // Re-check and update UI for the new login status
            this.handleUserLoginStatus();
        });
    }
    
    setupFloatingLabels() {
        // Handle floating labels for form inputs
        jQuery('.shuttle-checkout__input').each((index, element) => {
            const input = jQuery(element);
            
            // Check if input has value on page load
            if (input.val().trim() !== '') {
                input.addClass('shuttle-checkout__input--filled');
            }
            
            // Handle input events
            input.on('input blur', () => {
                if (input.val().trim() !== '') {
                    input.addClass('shuttle-checkout__input--filled');
                } else {
                    input.removeClass('shuttle-checkout__input--filled');
                }
            });
        });
        
        // Detect and handle autofilled inputs
        this.detectAutofill();
    }
    
    /**
     * Detect autofilled inputs and apply correct styling
     */
    detectAutofill() {
        // Check for autofilled inputs periodically (browsers don't always trigger events)
        const checkAutofill = () => {
            jQuery('.shuttle-checkout__input').each((index, element) => {
                const input = jQuery(element);
                let isAutofilled = false;
                
                // Check if input is autofilled (webkit browsers)
                try {
                    if (element.matches(':-webkit-autofill')) {
                        isAutofilled = true;
                    }
                } catch (e) {
                    // :-webkit-autofill not supported in this browser
                }
                
                // Firefox autofill detection (different approach)
                if (!isAutofilled && input.val().trim() !== '') {
                    try {
                        if (input.is(':-moz-autofill')) {
                            isAutofilled = true;
                        }
                    } catch (e) {
                        // :-moz-autofill not supported in this browser, skip
                    }
                }
                
                // Alternative Firefox detection - check for autofill styling
                if (!isAutofilled && input.val().trim() !== '') {
                    const computedStyle = window.getComputedStyle(element);
                    if (computedStyle.getPropertyValue('-moz-appearance') === 'textfield' && 
                        computedStyle.getPropertyValue('background-color') !== 'rgba(0, 0, 0, 0)' &&
                        computedStyle.getPropertyValue('background-color') !== 'transparent') {
                        // Likely autofilled in Firefox
                        isAutofilled = true;
                    }
                }
                
                if (isAutofilled || input.val().trim() !== '') {
                    input.addClass('shuttle-checkout__input--filled');
                } else {
                    input.removeClass('shuttle-checkout__input--filled');
                }
            });
        };
        
        // Initial check
        checkAutofill();
        
        // Check periodically for autofill (some browsers delay autofill)
        setTimeout(checkAutofill, 100);
        setTimeout(checkAutofill, 500);
        setTimeout(checkAutofill, 1000);
        
        // Listen for animation events (webkit autofill triggers animations)
        jQuery('.shuttle-checkout__input').on('animationstart', (e) => {
            if (e.originalEvent.animationName === 'onAutoFillStart') {
                jQuery(e.target).addClass('shuttle-checkout__input--filled');
            }
        });
    }
    
    /**
     * Setup password toggle functionality
     */
    setupPasswordToggle() {
        const passwordToggle = jQuery('.shuttle-checkout__password-toggle');
        const passwordInput = jQuery('#passenger-password');
        const showIcon = jQuery('.shuttle-checkout__eye-icon--show');
        const hideIcon = jQuery('.shuttle-checkout__eye-icon--hide');
        
        if (passwordToggle.length && passwordInput.length) {
            passwordToggle.on('click', (e) => {
                e.preventDefault();
                
                const currentType = passwordInput.attr('type');
                
                if (currentType === 'password') {
                    // Show password
                    passwordInput.attr('type', 'text');
                    showIcon.hide();
                    hideIcon.show();
                } else {
                    // Hide password
                    passwordInput.attr('type', 'password');
                    showIcon.show();
                    hideIcon.hide();
                }
            });
            
            // Setup automatic login triggers
            this.setupPasswordLoginTriggers(passwordInput);
        }
    }
    
    /**
     * Setup automatic login triggers for password field
     * Triggers login on:
     * 1. Enter key press
     * 2. Blur (focus lost) if password field has value (typed or prefilled)
     */
    setupPasswordLoginTriggers(passwordInput) {
        // Track if user actually typed in the password field
        let passwordWasTyped = false;
        let blurTimeout = null;
        
        // Track when user types in password field
        passwordInput.on('input', () => {
            passwordWasTyped = true;
            
            // Clear any validation error when user starts typing
            passwordInput.removeClass('shuttle-checkout__input--error');
            passwordInput.next('.shuttle-checkout__error-message').remove();
        });
        
        // Trigger login on Enter key press
        passwordInput.on('keydown', (e) => {
            if (e.key === 'Enter' || e.keyCode === 13) {
                e.preventDefault();
                
                // Clear any pending blur timeout
                if (blurTimeout) {
                    clearTimeout(blurTimeout);
                    blurTimeout = null;
                }
                
                // Don't trigger login if user is already logged in
                if (this.isUserLoggedIn) {
                    passwordWasTyped = false;
                    return;
                }
                
                this.handleUserLogin();
                passwordWasTyped = false; // Reset flag
            }
        });
        
        // Trigger login on blur if there's a password value
        passwordInput.on('blur', () => {
            const password = passwordInput.val();
            
            // Only proceed if there's a password (works for both typed and prefilled)
            if (!password || password.trim() === '') {
                passwordWasTyped = false;
                return;
            }
            
            // Don't trigger login if user is already logged in
            if (this.isUserLoggedIn) {
                passwordWasTyped = false;
                return;
            }
            
            // Don't trigger login if password field is hidden (shouldn't happen, but safety check)
            if (!passwordInput.is(':visible')) {
                passwordWasTyped = false;
                return;
            }
            
            // Wait 300ms to make sure user really left the field (not just clicking elsewhere temporarily)
            blurTimeout = setTimeout(() => {
                // Check again if password still has value and user isn't logged in
                if (password && password.trim() !== '' && !this.isUserLoggedIn) {
                    this.handleUserLogin();
                    passwordWasTyped = false; // Reset flag
                }
                blurTimeout = null;
            }, 300);
        });
        
        // If user focuses back on password field quickly, cancel the blur login
        passwordInput.on('focus', () => {
            if (blurTimeout) {
                clearTimeout(blurTimeout);
                blurTimeout = null;
            }
        });
    }
    
    /**
     * Setup email check functionality
     * Checks if user exists when they leave the email field
     */
    setupEmailCheck() {
        const emailInput = jQuery('#passenger-email');
        const emailField = jQuery('.shuttle-checkout__field--email');
        const passwordField = jQuery('.shuttle-checkout__field--password');
        const fbLoginButton = jQuery('.shuttle-checkout__facebook-button');
        
        if (!emailInput.length) {
            return;
        }
        
        // Track if we're currently checking to prevent duplicate requests
        let isChecking = false;
        let lastCheckedEmail = '';
        
        // Hide password field initially, but show FB login by default for guests
        passwordField.hide();
        fbLoginButton.show(); // Show FB login button for guest users by default
        
        // Restore form data from localStorage for guest users
        this.waitForElement('#passenger-phone').then(() => {
            this.restoreFormDataFromLocalStorage();
        });
        
        // Check email when user leaves the field
        emailInput.on('blur', async () => {
            const email = emailInput.val().trim();
            
            // Validate email format
            if (!email || !this.isValidEmail(email)) {
                this.removeEmailLoadingIndicator();
                return;
            }
            
            // Don't check if same email was just checked
            if (email === lastCheckedEmail) {
                return;
            }
            
            // Don't check if already checking
            if (isChecking) {
                return;
            }
            
            isChecking = true;
            lastCheckedEmail = email;
            
            // Show loading indicator
            this.showEmailLoadingIndicator();
            
            try {
                const response = await jQuery.ajax({
                    url: window.ajaxurl,
                    type: 'POST',
                    data: {
                        action: 'shuttle_check_user_exists',
                        email: email
                    },
                    dataType: 'json'
                });
                
                // Remove loading indicator
                this.removeEmailLoadingIndicator();
                
                if (response.success && response.data) {
                    if (response.data.user_exists) {
                        // User exists - show password field (FB login stays visible)
                        passwordField.fadeIn(300);
                        fbLoginButton.fadeIn(300); // Ensure it's visible
                    } else {
                        // New user - hide password field but keep FB login visible
                        passwordField.fadeOut(300);
                        fbLoginButton.fadeIn(300); // FB login always available for guests
                    }
                }
                
            } catch (error) {
                // Remove loading indicator
                this.removeEmailLoadingIndicator();
                
                // On error, show password field to allow user to proceed
                passwordField.fadeIn(300);
                fbLoginButton.fadeIn(300);
            } finally {
                isChecking = false;
            }
        });
    }
    
    /**
     * Show loading indicator on email field
     */
    showEmailLoadingIndicator() {
        const emailField = jQuery('.shuttle-checkout__field--email');
        
        // Remove any existing indicator
        this.removeEmailLoadingIndicator();
        
        // Add loading spinner
        const spinner = jQuery(`
            <div class="shuttle-checkout__email-loading" style="
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                display: flex;
                align-items: center;
                gap: 8px;
                pointer-events: none;
            ">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="animation: spin 1s linear infinite;">
                    <circle cx="12" cy="12" r="10" stroke="#0ea5e9" stroke-width="3" stroke-linecap="round" stroke-dasharray="31.4 31.4" stroke-dashoffset="0" opacity="0.25"/>
                    <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="#0ea5e9" stroke-width="3" stroke-linecap="round"/>
                </svg>
                <span style="font-size: 0.875rem; color: #64748b;">Checking...</span>
            </div>
        `);
        
        emailField.css('position', 'relative');
        emailField.append(spinner);
        
        // Add spin animation if not already present
        if (!jQuery('#shuttle-email-spinner-style').length) {
            jQuery('head').append(`
                <style id="shuttle-email-spinner-style">
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                </style>
            `);
        }
    }
    
    /**
     * Remove loading indicator from email field
     */
    removeEmailLoadingIndicator() {
        jQuery('.shuttle-checkout__email-loading').remove();
    }
    
    /**
     * Validate email format
     */
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    
    /**
     * Handle user login status and adapt UI
     */
    handleUserLoginStatus() {
        // Check if shuttleUserAuth is available
        if (typeof window.shuttleUserAuth === 'undefined') {
            // User auth not loaded, treat as guest and setup email check
            this.setupEmailCheck();
            return;
        }
        
        const userInfo = window.shuttleUserAuth.getUserInfo();
        
        if (userInfo.isLoggedIn) {
            // User is logged in
            this.handleLoggedInUser(userInfo);
        } else {
            // User is guest - setup email check
            this.setupEmailCheck();
        }
    }
    
    /**
     * Handle logged-in user UI and data prefilling
     */
    handleLoggedInUser(userInfo) {
        
        const emailField = jQuery('.shuttle-checkout__field--email');
        const emailInput = jQuery('#passenger-email');
        const passwordField = jQuery('.shuttle-checkout__field--password');
        const fbLoginButton = jQuery('.shuttle-checkout__facebook-login');
        
        // Hide email field, password field, and FB login for logged-in users
        emailField.hide();
        passwordField.hide();
        fbLoginButton.hide();
        
        // Ensure phone inputs are initialized before prefilling
        this.initializePhoneInputSafely();
        
        // Check if user has manually edited profile data
        const savedData = this.getLocalStorageCheckoutData();
        
        // Prefill user data when phone inputs are ready
        this.waitForElement('#passenger-phone').then(() => {
            // Always prefill profile data first
            this.prefillUserData(userInfo);
            
            // Then overlay edited fields from localStorage if user has made edits
            if (savedData && savedData.userEditedProfileData === true) {
                this.restoreFormDataFromLocalStorage();
            }
        });
        
        // Update legal text for logged-in users when element is ready
        this.waitForElement('.shuttle-checkout-summary__legal-text').then((element) => {
            this.updateLegalTextForLoggedInUser(element);
        });
        
        // Show logged-in status indicator
        this.showLoggedInStatus(userInfo);
    }
    
    /**
     * Prefill form fields with user data from cookie
     * Fields remain editable - changes won't update user profile, only used for this booking
     */
    prefillUserData(userInfo) {
        const userData = userInfo.userData;
        
        if (!userData) {
            return;
        }
        
        // Store original profile data for comparison when detecting user edits
        this.originalProfileData = {
            email: userData.email || '',
            firstName: userData.first_name || userData.firstname || userData.name || '',
            lastName: userData.last_name || userData.lastname || userData.surname || '',
            phone: userData.phone_number_1 || userData.phone || userData.phone_number || '',
            countryCode: userData.country_code_1 || ''
        };
        
        // Reset the userEditedProfileData flag
        this.checkoutData.userEditedProfileData = false;
        
        // Prefill email (hidden but still submitted with form)
        if (userData.email) {
            const emailInput = jQuery('#passenger-email');
            emailInput.val(userData.email);
            emailInput.addClass('shuttle-checkout__input--filled');
            // Remove required attribute since field is hidden
            emailInput.removeAttr('required');
        }
        
        // Prefill first name (editable)
        if (userData.first_name || userData.firstname || userData.name) {
            const firstNameInput = jQuery('#passenger-first-name');
            const firstName = userData.first_name || userData.firstname || userData.name;
            firstNameInput.val(firstName);
            firstNameInput.addClass('shuttle-checkout__input--filled');
        }
        
        // Prefill last name (editable)
        if (userData.last_name || userData.lastname || userData.surname) {
            const lastNameInput = jQuery('#passenger-last-name');
            const lastName = userData.last_name || userData.lastname || userData.surname;
            lastNameInput.val(lastName);
            lastNameInput.addClass('shuttle-checkout__input--filled');
        }
        
        // Prefill primary phone with country code (editable)
        if (userData.phone_number_1 || userData.phone || userData.phone_number) {
            const phoneInput = jQuery('#passenger-phone')[0]; // Get DOM element
            const phoneNumber = userData.phone_number_1 || userData.phone || userData.phone_number;
            const countryCode = userData.country_code_1; // e.g., "+371"
            
            // Set phone number and country code
            this.setPhoneNumberWithCountryCode(phoneInput, phoneNumber, countryCode);
            
            // Save profile data to localStorage so it persists on reload
            // Use a small delay to ensure the phone input instance has updated
            setTimeout(() => {
                const phoneValue = jQuery(phoneInput).val().trim();
                if (phoneValue) {
                    this.saveFormFieldToLocalStorage('phone', phoneValue);
                }
            }, 150);
        }
        
        // Prefill alternate phone with country code (editable)
        if (userData.phone_number_2) {
            const altPhoneInput = jQuery('#passenger-alternate-phone')[0]; // Get DOM element
            const phoneNumber = userData.phone_number_2;
            const countryCode = userData.country_code_2; // e.g., "+1 246"
            
            // Set phone number and country code
            this.setPhoneNumberWithCountryCode(altPhoneInput, phoneNumber, countryCode);
            
            // Save profile data to localStorage so it persists on reload
            setTimeout(() => {
                const phoneValue = jQuery(altPhoneInput).val().trim();
                if (phoneValue) {
                    this.saveFormFieldToLocalStorage('alternatePhone', phoneValue);
                }
            }, 150);
        }
    }
    
    /**
     * Set phone number with country code for custom phone input
     * @param {HTMLElement} phoneInputElement - The phone input DOM element
     * @param {string} phoneNumber - The phone number without country code
     * @param {string} countryCode - The country code (e.g., "+371", "+1 246")
     */
    setPhoneNumberWithCountryCode(phoneInputElement, phoneNumber, countryCode) {
        if (!phoneInputElement || !phoneNumber) {
            return;
        }
        
        const $phoneInput = jQuery(phoneInputElement);
        
        // Find the CustomPhoneInput instance
        let phoneInstance = null;
        
        // Check if it's the primary phone
        if (phoneInputElement.id === 'passenger-phone' && this.phoneInputInstance) {
            phoneInstance = this.phoneInputInstance;
        } else if (phoneInputElement.id === 'passenger-alternate-phone' && this.alternatePhoneInputInstance) {
            phoneInstance = this.alternatePhoneInputInstance;
        }
        
        if (phoneInstance && countryCode) {
            // Find country by dial code
            const country = phoneInstance.countries.find(c => {
                // Remove spaces from both for comparison
                const apiDialCode = countryCode.replace(/\s+/g, '');
                const countryDialCode = c.dialCode.replace(/\s+/g, '');
                return countryDialCode === apiDialCode;
            });
            
            if (country) {
                // Set the country first (this will update the selector button)
                phoneInstance.setCountry(country.code);
                
                // Clean the phone number - remove any existing dial code
                // The API might send "+371 28366610" or just "28366610"
                let cleanNumber = phoneNumber.trim();
                
                // Remove the country code if it's at the start
                const dialCodePattern = new RegExp(`^\\${country.dialCode.replace(/\s/g, '\\s*')}`);
                cleanNumber = cleanNumber.replace(dialCodePattern, '').trim();
                
                // Also try removing just the numeric part of dial code
                const numericDialCode = country.dialCode.replace(/\D/g, '');
                const numericPattern = new RegExp(`^\\+?${numericDialCode}\\s*`);
                cleanNumber = cleanNumber.replace(numericPattern, '').trim();
                
                // Now set the full phone number including dial code
                // Format: +371 28366610
                const fullPhoneNumber = `${country.dialCode} ${cleanNumber}`;
                $phoneInput.val(fullPhoneNumber);
                $phoneInput.addClass('shuttle-checkout__input--filled');
                
                // Trigger input event to update any listeners
                $phoneInput.trigger('input');
                return;
            }
        }
        
        // Fallback: Set the phone number as-is if country not found
        $phoneInput.val(phoneNumber);
        $phoneInput.addClass('shuttle-checkout__input--filled');
        
        // Trigger input event to update any listeners
        $phoneInput.trigger('input');
    }
    
    /**
     * Get checkout data from localStorage
     * @returns {Object|null} Parsed checkout data or null if not found
     */
    getLocalStorageCheckoutData() {
        try {
            const dataStr = localStorage.getItem('shuttle_checkout_data');
            if (dataStr) {
                return JSON.parse(dataStr);
            }
        } catch (e) {
            console.error('[ShuttleCheckout] Failed to parse localStorage data:', e);
        }
        return null;
    }
    
    /**
     * Restore form data from localStorage
     * Used for guest users and logged-in users who have edited their profile data
     */
    restoreFormDataFromLocalStorage() {
        const savedData = this.getLocalStorageCheckoutData();
        
        if (!savedData || !savedData.leadPassenger) {
            return;
        }
        
        const leadPassenger = savedData.leadPassenger;
        
        // Restore email (for guest users only - logged-in users have it hidden)
        if (leadPassenger.email && jQuery('#passenger-email').is(':visible')) {
            const emailInput = jQuery('#passenger-email');
            emailInput.val(leadPassenger.email);
            emailInput.addClass('shuttle-checkout__input--filled');
        }
        
        // Restore first name
        if (leadPassenger.firstName) {
            const firstNameInput = jQuery('#passenger-first-name');
            firstNameInput.val(leadPassenger.firstName);
            firstNameInput.addClass('shuttle-checkout__input--filled');
        }
        
        // Restore last name
        if (leadPassenger.lastName) {
            const lastNameInput = jQuery('#passenger-last-name');
            lastNameInput.val(leadPassenger.lastName);
            lastNameInput.addClass('shuttle-checkout__input--filled');
        }
        
        // Restore primary phone with country code
        if (leadPassenger.phone) {
            const phoneInput = jQuery('#passenger-phone')[0];
            
            // Try to restore country code first (ISO code like 'gb', 'de', etc.)
            if (leadPassenger.phoneCountryCode && this.phoneInputInstance) {
                this.phoneInputInstance.setCountry(leadPassenger.phoneCountryCode);
            }
            
            // Then set phone number with dial code
            this.setPhoneNumberWithCountryCode(
                phoneInput, 
                leadPassenger.phone, 
                leadPassenger.countryCode || '+44'
            );
        }
        
        // Restore alternate phone with country code (if exists)
        if (leadPassenger.alternatePhone) {
            const altPhoneInput = jQuery('#passenger-alternate-phone')[0];
            this.setPhoneNumberWithCountryCode(
                altPhoneInput, 
                leadPassenger.alternatePhone, 
                leadPassenger.alternateCountryCode || '+44'
            );
        }
        
        // Restore additional info
        if (savedData.additionalInfo) {
            const additionalInfoField = jQuery('#additional-info');
            additionalInfoField.val(savedData.additionalInfo);
        }
        
        // Update internal checkoutData
        this.checkoutData.leadPassenger = { ...this.checkoutData.leadPassenger, ...leadPassenger };
        this.checkoutData.additionalInfo = savedData.additionalInfo || '';
        if (savedData.userEditedProfileData !== undefined) {
            this.checkoutData.userEditedProfileData = savedData.userEditedProfileData;
        }
    }
    
    /**
     * Save a form field to localStorage
     * @param {string} fieldName - Name of the field (email, firstName, lastName, phone, alternatePhone, additionalInfo)
     * @param {*} value - Value to save
     */
    saveFormFieldToLocalStorage(fieldName, value) {
        try {
            // Update checkoutData
            if (fieldName === 'additionalInfo') {
                this.checkoutData.additionalInfo = value;
            } else if (fieldName === 'phone' || fieldName === 'alternatePhone') {
                // For phone fields, also save country code
                const isAlternate = fieldName === 'alternatePhone';
                const phoneInstance = isAlternate ? this.alternatePhoneInputInstance : this.phoneInputInstance;
                
                if (phoneInstance) {
                    const phoneNumber = phoneInstance.getPhoneNumberWithoutDialCode();
                    const selectedCountry = phoneInstance.getSelectedCountry();
                    const countryCode = selectedCountry ? selectedCountry.dialCode : '+44';
                    const countryIsoCode = selectedCountry ? selectedCountry.code : 'gb';
                    
                    if (isAlternate) {
                        this.checkoutData.leadPassenger.alternatePhone = phoneNumber;
                        this.checkoutData.leadPassenger.alternateCountryCode = countryCode;
                        this.checkoutData.leadPassenger.alternatePhoneCountryCode = countryIsoCode;
                    } else {
                        this.checkoutData.leadPassenger.phone = phoneNumber;
                        this.checkoutData.leadPassenger.countryCode = countryCode;
                        this.checkoutData.leadPassenger.phoneCountryCode = countryIsoCode;
                    }
                }
            } else {
                // Regular text fields
                this.checkoutData.leadPassenger[fieldName] = value;
            }
            
            // Check if user is logged in and has edited profile data
            if (this.originalProfileData && window.shuttleUserAuth) {
                const userInfo = window.shuttleUserAuth.getUserInfo();
                if (userInfo.isLoggedIn) {
                    // Compare current value with original profile value
                    const originalValue = this.originalProfileData[fieldName] || '';
                    if (value !== originalValue) {
                        this.checkoutData.userEditedProfileData = true;
                    }
                }
            }
            
            // Save to localStorage
            localStorage.setItem('shuttle_checkout_data', JSON.stringify(this.checkoutData));
        } catch (e) {
            console.error('[ShuttleCheckout] Failed to save field to localStorage:', e);
        }
    }
    
    /**
     * Setup auto-save for form fields
     * Attaches blur event listeners to save data to localStorage
     */
    setupFormAutoSave() {
        // Email field (guest users only - logged-in users have it hidden)
        jQuery('#passenger-email').on('blur', (e) => {
            const value = jQuery(e.target).val().trim();
            if (value) {
                this.saveFormFieldToLocalStorage('email', value);
            }
        });
        
        // First name field
        jQuery('#passenger-first-name').on('blur', (e) => {
            const value = jQuery(e.target).val().trim();
            if (value) {
                this.saveFormFieldToLocalStorage('firstName', value);
            }
        });
        
        // Last name field
        jQuery('#passenger-last-name').on('blur', (e) => {
            const value = jQuery(e.target).val().trim();
            if (value) {
                this.saveFormFieldToLocalStorage('lastName', value);
            }
        });
        
        // Primary phone field
        jQuery('#passenger-phone').on('blur', (e) => {
            const value = jQuery(e.target).val().trim();
            if (value) {
                this.saveFormFieldToLocalStorage('phone', value);
            }
        });
        
        // Alternate phone field
        jQuery('#passenger-alternate-phone').on('blur', (e) => {
            const value = jQuery(e.target).val().trim();
            if (value) {
                this.saveFormFieldToLocalStorage('alternatePhone', value);
            }
        });
        
        // Additional info field (also add blur handler for consistency)
        jQuery('#additional-info').on('blur', (e) => {
            const value = jQuery(e.target).val();
            this.saveFormFieldToLocalStorage('additionalInfo', value);
        });
    }
    
    /**
     * Show logged-in status indicator
     */
    showLoggedInStatus(userInfo) {
        const section = jQuery('.shuttle-checkout__section--lead-passenger');
        
        // Get user's name
        const userName = userInfo.userData?.name || 
                        userInfo.userData?.first_name || 
                        userInfo.userData?.firstname || 
                        'User';
        
        // Add a logged-in indicator at the top of the section
        if (!section.find('.shuttle-checkout__logged-in-banner').length) {
            const banner = jQuery(`
                <div class="shuttle-checkout__logged-in-banner" style="
                    background: #f0f9ff;
                    border: 1px solid #0ea5e9;
                    border-radius: 8px;
                    padding: 12px 16px;
                    margin-bottom: 20px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                ">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="#0ea5e9"/>
                    </svg>
                    <span style="color: #0c4a6e; font-size: 0.9375rem;">
                        ${this.getTranslatedString('welcomeBack', [`<strong>${userName}</strong>`])}
                    </span>
                </div>
            `);
            section.find('.shuttle-checkout__section-description').after(banner);
        }
    }
    
    /**
     * Wait for DOM element to exist
     * @param {string} selector - jQuery selector
     * @param {number} timeout - Maximum wait time in ms (default 5000)
     * @returns {Promise}
     */
    waitForElement(selector, timeout = 5000) {
        return new Promise((resolve, reject) => {
            // Check if element already exists
            const element = jQuery(selector);
            if (element.length > 0) {
                resolve(element);
                return;
            }
            
            // Use MutationObserver to watch for element
            const observer = new MutationObserver((mutations, obs) => {
                const element = jQuery(selector);
                if (element.length > 0) {
                    obs.disconnect();
                    resolve(element);
                }
            });
            
            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
            
            // Timeout fallback
            setTimeout(() => {
                observer.disconnect();
                const element = jQuery(selector);
                if (element.length > 0) {
                    resolve(element);
                } else {
                    reject(new Error(`Element ${selector} not found within ${timeout}ms`));
                }
            }, timeout);
        });
    }
    
    /**
     * Update legal text for logged-in users
     * Logged-in users see simpler text without account creation notice
     * @param {jQuery} legalTextElement - Optional jQuery element to update (if already found)
     */
    updateLegalTextForLoggedInUser(legalTextElement = null) {
        // Use provided element or search for it
        const legalText = legalTextElement || jQuery('.shuttle-checkout-summary__legal-text');
        
        if (legalText && legalText.length) {
            // Get URLs from window.shuttleLegalUrls (set in page template)
            const termsUrl = window.shuttleLegalUrls?.terms || '/terms-conditions/';
            const privacyUrl = window.shuttleLegalUrls?.privacy || '/privacy-policy/';
            
            // Simpler text for logged-in users (no account creation notice)
            const newText = `
                By clicking 'Continue to Payment', you agree to our 
                <a href="${termsUrl}" target="_blank">Terms and Conditions</a> and 
                <a href="${privacyUrl}" target="_blank">Privacy Policy</a>.
            `;
            
            legalText.html(newText);
        }
    }
    
    initializePhoneInputSafely() {
        // Safely initialize phone input with proper error handling
        try {
            if (typeof window.CustomPhoneInput === 'undefined') {
                console.warn('CustomPhoneInput class not available');
                return;
            }
            
            // Initialize primary phone input
            const phoneInput = document.querySelector('#passenger-phone');
            if (phoneInput) {
                // Check if already initialized to prevent duplicates
                if (phoneInput.hasAttribute('data-custom-phone-initialized')) {
                    return;
                }
                
                // Initialize with error handling
                this.phoneInputInstance = new window.CustomPhoneInput(phoneInput, {
                    preferredCountries: ['gb', 'us', 'fr', 'de', 'it', 'es', 'ch', 'at']
                });
                
                phoneInput.setAttribute('data-custom-phone-initialized', 'true');
                
                // Handle floating label for primary phone input
                phoneInput.addEventListener('countrychange', () => {
                    this.updateFloatingLabel(phoneInput);
                });
                
                // Additional event listeners for phone input
                phoneInput.addEventListener('input', () => {
                    this.updateFloatingLabel(phoneInput);
                });
                phoneInput.addEventListener('keyup', () => {
                    this.updateFloatingLabel(phoneInput);
                });
                phoneInput.addEventListener('paste', () => {
                    setTimeout(() => this.updateFloatingLabel(phoneInput), 10);
                });
            }
            
            // Initialize alternate phone input
            const alternatePhoneInput = document.querySelector('#passenger-alternate-phone');
            if (alternatePhoneInput) {
                // Check if already initialized to prevent duplicates
                if (alternatePhoneInput.hasAttribute('data-custom-phone-initialized')) {
                    return;
                }
                
                this.alternatePhoneInputInstance = new window.CustomPhoneInput(alternatePhoneInput, {
                    preferredCountries: ['gb', 'us', 'fr', 'de', 'it', 'es', 'ch', 'at']
                });
                
                alternatePhoneInput.setAttribute('data-custom-phone-initialized', 'true');
                
                // Handle floating label for alternate phone input
                alternatePhoneInput.addEventListener('countrychange', () => {
                    this.updateFloatingLabel(alternatePhoneInput);
                });
                
                // Additional event listeners for alternate phone input
                alternatePhoneInput.addEventListener('input', () => {
                    this.updateFloatingLabel(alternatePhoneInput);
                });
                alternatePhoneInput.addEventListener('keyup', () => {
                    this.updateFloatingLabel(alternatePhoneInput);
                });
                alternatePhoneInput.addEventListener('paste', () => {
                    setTimeout(() => this.updateFloatingLabel(alternatePhoneInput), 10);
                });
            }
            
        } catch (error) {
            console.warn('Phone input initialization failed, using basic input:', error);
            // Fallback to basic phone input functionality
            this.setupBasicPhoneInputs();
        }
    }
    
    setupBasicPhoneInputs() {
        // Fallback phone input setup without country selector
        const phoneInputs = document.querySelectorAll('.shuttle-checkout__input--phone');
        phoneInputs.forEach(input => {
            input.addEventListener('input', () => {
                this.updateFloatingLabel(input);
            });
            input.addEventListener('keyup', () => {
                this.updateFloatingLabel(input);
            });
            input.addEventListener('paste', () => {
                setTimeout(() => this.updateFloatingLabel(input), 10);
            });
        });
    }
    
    initializeBusStops() {
        const section = jQuery('.shuttle-checkout__section--bus-stops');
        const container = jQuery('#bus-stops-container');
        const descriptionElement = section.find('.shuttle-checkout__section-description');
        
        // Hide section if no bus stops should be shown
        if (!this.shouldShowBusStops || !this.busStops || this.busStops.length === 0) {
            section.hide();
            this.checkoutData.busStop = null;
            return;
        }
        
        // Show section and populate stops
        section.show();
        container.empty();
        
        // Update description text based on transfer type and direction
        const descriptionText = this.getBusStopDescriptionText();
        descriptionElement.text(descriptionText);
        
        this.busStops.forEach((stop, index) => {
            const stopHtml = `
                <div class="shuttle-checkout__bus-stop" data-stop-id="${stop.id}">
                    <input type="radio" 
                           id="bus-stop-${stop.id}" 
                           name="bus_stop" 
                           value="${stop.id}" 
                           class="shuttle-checkout__bus-stop-radio">
                    <div class="shuttle-checkout__bus-stop-info">
                        <p class="shuttle-checkout__bus-stop-text">${stop.name}</p>
                    </div>
                </div>
            `;
            container.append(stopHtml);
        });
        
        // No default selection - user must choose
        this.checkoutData.busStop = null;
        
        // Add click handlers
        container.on('click', '.shuttle-checkout__bus-stop', (e) => {
            const stopId = jQuery(e.currentTarget).data('stop-id');
            this.selectBusStop(stopId);
        });
    }
    
    /**
     * Get appropriate description text for bus stop selection
     * Based on transfer type (one-way/return) and which location is the resort
     */
    getBusStopDescriptionText() {
        const transferType = this.bookingData.transferType;
        
        // Helper to extract ID
        const extractId = (obj) => {
            if (!obj) return null;
            if (typeof obj === 'string') return obj;
            if (obj.id) return obj.id;
            return null;
        };
        
        // Helper to check if ID is a resort
        const isResort = (id) => {
            return id && id.toString().startsWith('resort-');
        };
        
        const fromId = extractId(this.bookingData.from);
        const toId = extractId(this.bookingData.to);
        
        const resortIsOrigin = isResort(fromId);
        const resortIsDestination = isResort(toId);
        
        if (transferType === 'return') {
            // Two-way trip
            return this.getTranslatedString('busStopDescReturn');
        } else {
            // One-way trip
            if (resortIsDestination) {
                // Airport -> Resort (drop off at resort)
                return this.getTranslatedString('busStopDescDropOff');
            } else if (resortIsOrigin) {
                // Resort -> Airport (pick up from resort)
                return this.getTranslatedString('busStopDescPickUp');
            }
        }
        
        // Fallback
        return this.getTranslatedString('busStopDescDefault');
    }
    
    selectBusStop(stopId) {
        const clickedStop = jQuery(`.shuttle-checkout__bus-stop[data-stop-id="${stopId}"]`);
        const clickedRadio = jQuery(`#bus-stop-${stopId}`);
        const isCurrentlySelected = clickedRadio.prop('checked');
        
        if (isCurrentlySelected) {
            // Deselect if already selected
            jQuery('.shuttle-checkout__bus-stop').removeClass('shuttle-checkout__bus-stop--selected');
            jQuery('input[name="bus_stop"]').prop('checked', false);
            this.checkoutData.busStop = null;
            
            // Remove from bookingData and localStorage
            delete this.bookingData.selectedBusStop;
            localStorage.setItem('shuttle_booking_data', JSON.stringify(this.bookingData));
        } else {
            // Select the clicked stop
            jQuery('.shuttle-checkout__bus-stop').removeClass('shuttle-checkout__bus-stop--selected');
            clickedStop.addClass('shuttle-checkout__bus-stop--selected');
            clickedRadio.closest('.shuttle-checkout__bus-stop').addClass('shuttle-checkout__bus-stop--selected');
            
            jQuery('input[name="bus_stop"]').prop('checked', false);
            clickedRadio.prop('checked', true);
            
            this.checkoutData.busStop = stopId;
            
            // Save to bookingData and localStorage
            this.bookingData.selectedBusStop = stopId;
            localStorage.setItem('shuttle_booking_data', JSON.stringify(this.bookingData));
        }
        
        // Update summary
        this.updateBookingSummary();
    }
    
    initializeFareConditions() {
        // Fare conditions are now in PHP template, just update dynamic content and set up event listeners
        
        // Update prices for current currency (if not EUR)
        this.updateFareConditionPrices();
        
        // Set up event listeners for the static template
        this.setupFareConditionsEventListeners();
        
        // Sync button texts with translated strings on initialization
        this.syncFareButtonTexts();
        
        // Update luggage info section based on current fare condition
        const currentFareCondition = this.bookingData.pricePreferences?.tier || 'skier';
        this.updateLuggageInfoSection(currentFareCondition);
    }
    
    /**
     * Sync fare condition button texts
     * Weglot will automatically translate "Choose" and "Chosen" via dynamic selectors
     */
    syncFareButtonTexts() {
        // Update all buttons based on their current state
        // Weglot will translate these automatically
        jQuery('.shuttle-checkout__fare-option-button').each(function() {
            const isSelected = jQuery(this).hasClass('shuttle-checkout__fare-option-button--selected');
            jQuery(this).text(isSelected ? 'Chosen' : 'Choose');
        });
    }
    
    setupFareConditionsEventListeners() {
        // Use document-level event delegation for static template
        jQuery(document).on('click', '.shuttle-checkout__fare-option', (e) => {
            // Don't trigger if clicking the button directly
            if (!jQuery(e.target).hasClass('shuttle-checkout__fare-option-button')) {
                const fareId = jQuery(e.currentTarget).data('fare-id');
                this.selectFareCondition(fareId);
            }
        });
        
        jQuery(document).on('click', '.shuttle-checkout__fare-option-button', (e) => {
            e.stopPropagation();
            const fareId = jQuery(e.currentTarget).data('fare-id');
            this.selectFareCondition(fareId);
        });
    }
    
    selectFareCondition(fareId) {
        // Update UI - remove selected state from all options
        jQuery('.shuttle-checkout__fare-option').removeClass('shuttle-checkout__fare-option--selected');
        jQuery('.shuttle-checkout__fare-option-button').removeClass('shuttle-checkout__fare-option-button--selected shuttle-checkout__fare-option-button--unselected');
        
        // Add selected state to chosen option
        jQuery(`.shuttle-checkout__fare-option[data-fare-id="${fareId}"]`).addClass('shuttle-checkout__fare-option--selected');
        
        // Update all buttons to "Choose" state
        // Weglot will automatically translate these via dynamic selectors
        jQuery('.shuttle-checkout__fare-option-button').addClass('shuttle-checkout__fare-option-button--unselected').text('Choose');
        
        // Update selected button to "Chosen" state
        // Weglot will automatically translate this via dynamic selectors
        jQuery(`.shuttle-checkout__fare-option-button[data-fare-id="${fareId}"]`)
            .removeClass('shuttle-checkout__fare-option-button--unselected')
            .addClass('shuttle-checkout__fare-option-button--selected')
            .text('Chosen');
        
        // Update radio button
        jQuery('input[name="fare_condition"]').prop('checked', false);
        jQuery(`#fare-${fareId}`).prop('checked', true);
        
        // Update checkout data
        this.checkoutData.fareCondition = fareId;
        
        // Update booking data pricePreferences
        if (!this.bookingData.pricePreferences) {
            this.bookingData.pricePreferences = {};
        }
        this.bookingData.pricePreferences.tier = fareId;
        
        // Save to localStorage
        localStorage.setItem('shuttle_booking_data', JSON.stringify(this.bookingData));
        
        // Track GA event: fare_choosen (Row 12)
        if (window.ShuttleGAEvents) {
            const gaEvents = new window.ShuttleGAEvents();
            gaEvents.trackFareChosen(fareId);
        }
        
        // Update luggage info section based on selected fare condition
        this.updateLuggageInfoSection(fareId);
        
        // Update summary
        this.updateBookingSummary();
    }
    
    updateLuggageInfoSection(fareId) {
        // Show/hide ski/snowboard bag info based on fare condition
        const skiInfoItem = jQuery('.shuttle-checkout__luggage-info-item--ski');
        
        if (fareId === 'skier' || fareId === 'pro') {
            // Show ski/snowboard bag for skier and pro fare conditions
            skiInfoItem.show();
        } else {
            // Hide ski/snowboard bag for basic fare condition
            skiInfoItem.hide();
        }
    }
    
    calculateBaseFarePerPerson() {
        const selectedRoutes = this.bookingData.selectedRoutes;
        let baseFarePerPerson = 0;
        
        // Get base fare from outbound route
        if (selectedRoutes.outbound) {
            baseFarePerPerson = selectedRoutes.outbound.price.amount;
        }
        
        return baseFarePerPerson;
    }
    
    calculateSeatsFareForTripType(tripType) {
        const selectedRoutes = this.bookingData.selectedRoutes;
        const currency = this.bookingData.currency || 'EUR';
        
        let basePrice = 0;
        if (tripType === 'return' && selectedRoutes.return) {
            // Return trip: use return route price
            basePrice = selectedRoutes.return.price.amount;
        } else if (selectedRoutes.outbound) {
            // Outbound or one-way: use outbound route price
            basePrice = selectedRoutes.outbound.price.amount;
        }
        
        // Convert from EUR to current currency and round to 2 decimal places
        return Math.round(this.currencyManager.convertPrice(basePrice, 'EUR', currency) * 100) / 100;
    }
    
    calculateTotalBaseFare(totalPassengers) {
        const selectedRoutes = this.bookingData.selectedRoutes;
        const currency = this.bookingData.currency || 'EUR';
        let totalBaseFareInEUR = 0;
        
        // Add outbound fare
        if (selectedRoutes.outbound) {
            totalBaseFareInEUR += selectedRoutes.outbound.price.amount * totalPassengers;
        }
        
        // Add return fare if it exists
        if (selectedRoutes.return) {
            totalBaseFareInEUR += selectedRoutes.return.price.amount * totalPassengers;
        }
        
        // Convert from EUR to current currency and round to 2 decimal places
        return Math.round(this.currencyManager.convertPrice(totalBaseFareInEUR, 'EUR', currency) * 100) / 100;
    }
    
    /**
     * Calculate base fare by summing individual converted route prices
     * This matches exactly what's displayed in the summary
     */
    calculateBaseFareFromConvertedRoutes(totalPassengers) {
        const selectedRoutes = this.bookingData.selectedRoutes;
        let total = 0;
        
        // Add outbound fare (convert individually like in summary)
        if (selectedRoutes.outbound) {
            const outboundFareConverted = this.calculateSeatsFareForTripType('outbound');
            total += outboundFareConverted * totalPassengers;
        }
        
        // Add return fare if it exists (convert individually like in summary)
        if (selectedRoutes.return) {
            const returnFareConverted = this.calculateSeatsFareForTripType('return');
            total += returnFareConverted * totalPassengers;
        }
        
        // Round the final total
        return Math.round(total * 100) / 100;
    }
    
    initializeLuggage() {
        // Luggage info and options are now in PHP template, just update dynamic content
        
        // Define luggage options data (minimal data since PHP template has most info)
        const luggageOptions = [
            {
                id: 'additional_bag',
                price: this.extrasData.luggage?.price || 20
            },
            {
                id: 'ski_bag',
                price: this.extrasData.ski?.price || 20
            }
        ];
        
        // Update static template with dynamic data (prices and currency)
        luggageOptions.forEach(luggage => {
            this.updateLuggageOption(luggage);
        });
        
        // Add event listeners for the static template
        this.setupLuggageEventListeners();
    }
    
    updateLuggageOption(luggage) {
        const optionElement = jQuery(`[data-luggage-id="${luggage.id}"]`);
        if (optionElement.length === 0) return;
        
        const currentQuantity = luggage.id === 'additional_bag' ? 
            this.checkoutData.luggage.additional_bag : 
            this.checkoutData.luggage.ski_bag;
        
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);
        const hasReturn = this.bookingData.selectedRoutes.return;
        
        // Get base price from PHP template data attribute (already from API cache)
        const basePriceElement = optionElement.find('.price-amount');
        const basePrice = parseFloat(basePriceElement.attr('data-base-price')) || luggage.price || 20;
        
        // Always update prices based on actual trip type (PHP renders with default assumptions)
        // Convert price from EUR to current currency and round to 2 decimal places
        const convertedPrice = Math.round(this.currencyManager.convertPrice(basePrice, 'EUR', currency) * 100) / 100;
        
        // Calculate display price based on trip type
        const displayPrice = hasReturn ? convertedPrice * 2 : convertedPrice;
        
        // Update price display
        optionElement.find('.currency-symbol').text(currencySymbol);
        optionElement.find('.price-amount').text(this.formatPrice(displayPrice));
        
        // Update tooltip with one-way price
        const tooltipBasePrice = parseFloat(optionElement.find('.shuttle-checkout__luggage-option-info-icon').attr('data-tooltip-base')) || basePrice;
        const convertedTooltipPrice = Math.round(this.currencyManager.convertPrice(tooltipBasePrice, 'EUR', currency) * 100) / 100;
        const tooltipText = `${currencySymbol}${this.formatPrice(convertedTooltipPrice)}/${this.getTranslationString('one-way')}`;
        optionElement.find('.shuttle-checkout__luggage-option-info-icon').attr('data-tooltip', tooltipText);
        
        // Update trip type text
        const tripTypeText = hasReturn ? this.getTranslationString('return-trip') : this.getTranslationString('one-way');
        optionElement.find('.shuttle-checkout__luggage-option-trip-type').text(tripTypeText);
        
        // Update quantity input
        optionElement.find('.shuttle-checkout__luggage-quantity-input').val(currentQuantity);
        
        // Update button states
        const decreaseBtn = optionElement.find('[data-action="decrease"]');
        const increaseBtn = optionElement.find('[data-action="increase"]');
        
        decreaseBtn.prop('disabled', currentQuantity <= 0);
        increaseBtn.prop('disabled', false); // No max limit
        
        // Option is already visible from PHP template
    }
    
    getTranslationString(key) {
        // Get translated strings from the hidden translation elements
        const translationElements = jQuery(`.shuttle-translation-strings [data-string="${key}"]`);
        
        if (translationElements.length > 0) {
            // Use only the first element to avoid concatenation
            return jQuery(translationElements[0]).text();
        }
        
        return key;
    }
    
    // Old luggage info methods removed - now in PHP template
    
    getAssetUrl(path) {
        // Get the theme URL correctly
        const siteUrl = window.location.origin;
        return `${siteUrl}/wp-content/themes/a2a_theme/assets/${path}`;
    }
    
    setupLuggageEventListeners() {
        // Use document-level event delegation for static template
        jQuery(document).on('click', '.shuttle-checkout__luggage-quantity-btn', (e) => {
            const action = jQuery(e.currentTarget).data('action');
            const luggageId = jQuery(e.currentTarget).data('luggage-id');
            this.updateLuggageQuantity(luggageId, action);
        });
        
        // Input change handlers
        jQuery(document).on('change', '.shuttle-checkout__luggage-quantity-input', (e) => {
            const luggageId = jQuery(e.currentTarget).data('luggage-id');
            const inputValue = jQuery(e.currentTarget).val();
            const quantity = Math.max(0, parseInt(inputValue) || 0);
            this.setLuggageQuantity(luggageId, quantity);
        });
        
        // Tooltip handlers
        jQuery(document).on('click', '.shuttle-checkout__luggage-option-info-icon', (e) => {
            this.showLuggageTooltip(e.currentTarget);
        });
        
        // Close tooltip when clicking outside
        jQuery(document).on('click', (e) => {
            if (!jQuery(e.target).closest('.shuttle-checkout__luggage-option-info-icon, .shuttle-checkout__luggage-tooltip').length) {
                jQuery('.shuttle-checkout__luggage-tooltip').removeClass('show');
            }
        });
    }
    
    showLuggageTooltip(iconElement) {
        // Use .attr() instead of .data() to get fresh value (jQuery caches .data() values)
        const tooltipText = jQuery(iconElement).attr('data-tooltip');
        const existingTooltip = jQuery('.shuttle-checkout__luggage-tooltip');
        
        if (existingTooltip.length) {
            existingTooltip.remove();
        }
        
        const tooltipHtml = `
            <div class="shuttle-checkout__luggage-tooltip show">
                <div class="shuttle-checkout__luggage-tooltip-content">${tooltipText}</div>
            </div>
        `;
        
        jQuery(iconElement).append(tooltipHtml);
    }
    
    getIncludedLuggage() {
        const fareCondition = this.checkoutData.fareCondition;
        
        // Use API data if available, otherwise fallback to defaults
        const luggageIncluded = this.extrasData.luggage?.included || {
            'basic': 1, 'skier': 1, 'pro': 1
        };
        const skiIncluded = this.extrasData.ski?.included || {
            'basic': 0, 'skier': 1, 'pro': 1
        };
        
        return {
            regularBags: luggageIncluded[fareCondition] || 1,
            skiBags: skiIncluded[fareCondition] || 0
        };
    }
    
    updateLuggageQuantity(luggageId, action) {
        const currentQuantity = luggageId === 'additional_bag' ? 
            this.checkoutData.luggage.additional_bag : 
            this.checkoutData.luggage.ski_bag;
        
        let newQuantity = currentQuantity;
        
        if (action === 'increase') {
            newQuantity = currentQuantity + 1;
        } else if (action === 'decrease' && currentQuantity > 0) {
            newQuantity = currentQuantity - 1;
        }
        
        this.setLuggageQuantity(luggageId, newQuantity);
    }
    
    setLuggageQuantity(luggageId, quantity) {
        const safeQuantity = Math.max(0, quantity);
        
        // Update checkout data
        if (luggageId === 'additional_bag') {
            this.checkoutData.luggage.additional_bag = safeQuantity;
        } else if (luggageId === 'ski_bag') {
            this.checkoutData.luggage.ski_bag = safeQuantity;
        }
        
        // Update booking data and save to localStorage
        if (!this.bookingData.luggage) {
            this.bookingData.luggage = { additional_bag: 0, ski_bag: 0 };
        }
        if (luggageId === 'additional_bag') {
            this.bookingData.luggage.additional_bag = safeQuantity;
        } else if (luggageId === 'ski_bag') {
            this.bookingData.luggage.ski_bag = safeQuantity;
        }
        localStorage.setItem('shuttle_booking_data', JSON.stringify(this.bookingData));
        
        // Track GA event: luggage_choosen (Row 13)
        if (window.ShuttleGAEvents) {
            const gaEvents = new window.ShuttleGAEvents();
            gaEvents.trackLuggageChosen(
                this.bookingData.luggage.additional_bag,
                this.bookingData.luggage.ski_bag
            );
        }
        
        // Update UI
        const input = jQuery(`.shuttle-checkout__luggage-quantity-input[data-luggage-id="${luggageId}"]`);
        if (input.length > 0) {
            input.val(safeQuantity);
        }
        
        // Update buttons
        const decreaseBtn = jQuery(`.shuttle-checkout__luggage-quantity-btn[data-action="decrease"][data-luggage-id="${luggageId}"]`);
        const increaseBtn = jQuery(`.shuttle-checkout__luggage-quantity-btn[data-action="increase"][data-luggage-id="${luggageId}"]`);
        
        if (decreaseBtn.length > 0) {
            decreaseBtn.prop('disabled', safeQuantity <= 0);
        }
        if (increaseBtn.length > 0) {
            increaseBtn.prop('disabled', false); // No max limit for luggage
        }
        
        // Update total for this item
        const price = luggageId === 'additional_bag' ? 
            (this.extrasData.luggage?.price || 20) : 
            (this.extrasData.ski?.price || 20);
        
        // Update booking summary
        this.updateBookingSummary();
    }
    
    initializeChildSeats() {
        // Check if children are in the booking
        const passengers = this.bookingData.passengers;
        const hasChildren = passengers.children && passengers.children > 0;
        
        if (hasChildren) {
            // Show the child seats section
            jQuery('#child-seats-section').show();
            
            // Child seats are now in PHP template, just update dynamic content
            const childSeatOptions = [
                {
                    id: 'booster',
                    price: this.extrasData.booster_seat?.price || 2
                },
                {
                    id: 'regular',
                    price: this.extrasData.child_seat?.price || 2
                }
            ];
            
            // Update static template with dynamic data (prices and currency)
            childSeatOptions.forEach(seat => {
                this.updateChildSeatOption(seat);
            });
            
            // Add event listeners for the static template
            this.setupChildSeatsEventListeners();
        } else {
            // Hide the child seats section if no children
            jQuery('#child-seats-section').hide();
        }
    }
    
    updateChildSeatOption(seat) {
        const optionElement = jQuery(`[data-seat-id="${seat.id}"]`);
        if (optionElement.length === 0) return;
        
        const currentQuantity = seat.id === 'regular' ? 
            this.checkoutData.childSeats.regular : 
            this.checkoutData.childSeats.booster;
        
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);
        const hasReturn = this.bookingData.selectedRoutes.return;
        
        // Get base price from PHP template data attribute (already from API cache)
        const basePriceElement = optionElement.find('.price-amount');
        const basePrice = parseFloat(basePriceElement.attr('data-base-price')) || seat.price || 2;
        
        // Always update prices based on actual trip type (PHP renders with default assumptions)
        // Convert price from EUR to current currency and round to 2 decimal places
        const convertedPrice = Math.round(this.currencyManager.convertPrice(basePrice, 'EUR', currency) * 100) / 100;
        
        // Calculate display price based on trip type
        const displayPrice = hasReturn ? convertedPrice * 2 : convertedPrice;
        
        // Update price display
        optionElement.find('.currency-symbol').text(currencySymbol);
        optionElement.find('.price-amount').text(this.formatPrice(displayPrice));
        
        // Update tooltip with one-way price
        const tooltipBasePrice = parseFloat(optionElement.find('.shuttle-checkout__child-seat-option-info-icon').attr('data-tooltip-base')) || basePrice;
        const convertedTooltipPrice = Math.round(this.currencyManager.convertPrice(tooltipBasePrice, 'EUR', currency) * 100) / 100;
        const tooltipText = `${currencySymbol}${this.formatPrice(convertedTooltipPrice)}/${this.getTranslationString('one-way')}`;
        optionElement.find('.shuttle-checkout__child-seat-option-info-icon').attr('data-tooltip', tooltipText);
        
        // Update trip type text
        const tripTypeText = hasReturn ? this.getTranslationString('return-trip') : this.getTranslationString('one-way');
        optionElement.find('.shuttle-checkout__child-seat-option-trip-type').text(tripTypeText);
        
        // Update quantity input
        optionElement.find('.shuttle-checkout__child-seat-quantity-input').val(currentQuantity);
        
        // Update button states
        const decreaseBtn = optionElement.find('[data-action="decrease"]');
        const increaseBtn = optionElement.find('[data-action="increase"]');
        
        decreaseBtn.prop('disabled', currentQuantity <= 0);
        increaseBtn.prop('disabled', false); // No max limit
        
        // Option is already visible from PHP template
    }
    
    // Old renderChildSeats method removed - now using static PHP template
    
    // Old addChildSeatsAlert method removed - now in PHP template
    
    correctPHPDefaults() {
        // PHP now handles initial display correctly, so minimal corrections needed
        const actualData = this.bookingData;
        
        // Correct trip type text for luggage and child seats
        const isReturnTrip = actualData.transferType === 'return';
        const tripTypeText = isReturnTrip ? 'Round trip' : 'One-way';
        jQuery('.shuttle-checkout__luggage-option-trip-type, .shuttle-checkout__child-seat-option-trip-type').text(tripTypeText);
        
        // Only correct child seats visibility based on actual children count
        const hasChildren = actualData.passengers?.children > 0;
        if (hasChildren) {
            jQuery('#child-seats-section').show();
        } else {
            jQuery('#child-seats-section').hide();
        }
        
        // Restore fare selection from localStorage
        if (actualData.pricePreferences?.tier) {
            this.selectFareCondition(actualData.pricePreferences.tier);
        }
        
        // Restore luggage selections from localStorage
        if (actualData.luggage) {
            if (actualData.luggage.additional_bag) {
                this.setLuggageQuantity('additional_bag', actualData.luggage.additional_bag);
            }
            if (actualData.luggage.ski_bag) {
                this.setLuggageQuantity('ski_bag', actualData.luggage.ski_bag);
            }
        }
        
        // Restore child seat selections from localStorage
        if (actualData.childSeats) {
            if (actualData.childSeats.regular) {
                this.setChildSeatQuantity('regular', actualData.childSeats.regular);
            }
            if (actualData.childSeats.booster) {
                this.setChildSeatQuantity('booster', actualData.childSeats.booster);
            }
        }
        
        // Restore bus stop selection from localStorage
        if (actualData.selectedBusStop) {
            // Wait a bit for bus stops to be rendered, then restore selection
            setTimeout(() => {
                const busStopExists = jQuery(`.shuttle-checkout__bus-stop[data-stop-id="${actualData.selectedBusStop}"]`).length > 0;
                if (busStopExists) {
                    this.selectBusStop(actualData.selectedBusStop);
                }
            }, 100);
        }
        
        // Restore additional comment from shuttle_checkout_data localStorage
        this.restoreAdditionalComment();
        
    }
    
    /**
     * Restore additional comment field from localStorage
     * Uses shuttle_checkout_data to persist user's comment across page reloads
     */
    restoreAdditionalComment() {
        try {
            const checkoutDataStr = localStorage.getItem('shuttle_checkout_data');
            if (checkoutDataStr) {
                const checkoutData = JSON.parse(checkoutDataStr);
                if (checkoutData.additionalInfo) {
                    const commentField = jQuery('#additional-info');
                    if (commentField.length) {
                        commentField.val(checkoutData.additionalInfo);
                    }
                }
            }
        } catch (e) {
            console.error('[ShuttleCheckout] Failed to restore additional comment:', e);
        }
    }
    
    setupChildSeatsEventListeners() {
        // Use document-level event delegation for static template
        jQuery(document).on('click', '.shuttle-checkout__child-seat-quantity-btn', (e) => {
            const action = jQuery(e.currentTarget).data('action');
            const seatId = jQuery(e.currentTarget).data('seat-id');
            this.updateChildSeatQuantity(seatId, action);
        });
        
        // Input change handlers
        jQuery(document).on('change', '.shuttle-checkout__child-seat-quantity-input', (e) => {
            const seatId = jQuery(e.currentTarget).data('seat-id');
            const quantity = Math.max(0, parseInt(jQuery(e.currentTarget).val()) || 0);
            this.setChildSeatQuantity(seatId, quantity);
        });
        
        // Tooltip handlers
        jQuery(document).on('click', '.shuttle-checkout__child-seat-option-info-icon', (e) => {
            this.showChildSeatTooltip(e.currentTarget);
        });
        
        // Close tooltip when clicking outside
        jQuery(document).on('click', (e) => {
            if (!jQuery(e.target).closest('.shuttle-checkout__child-seat-option-info-icon, .shuttle-checkout__child-seat-tooltip').length) {
                jQuery('.shuttle-checkout__child-seat-tooltip').removeClass('show');
            }
        });
    }
    
    updateChildSeatQuantity(seatId, action) {
        const currentQuantity = seatId === 'regular' ? 
            this.checkoutData.childSeats.regular : 
            this.checkoutData.childSeats.booster;
        
        let newQuantity = currentQuantity;
        if (action === 'increase') {
            newQuantity = currentQuantity + 1;
        } else if (action === 'decrease') {
            newQuantity = Math.max(0, currentQuantity - 1);
        }
        
        this.setChildSeatQuantity(seatId, newQuantity);
    }
    
    setChildSeatQuantity(seatId, quantity) {
        // Update checkout data
        if (seatId === 'regular') {
            this.checkoutData.childSeats.regular = quantity;
        } else {
            this.checkoutData.childSeats.booster = quantity;
        }
        
        // Update booking data and save to localStorage
        if (!this.bookingData.childSeats) {
            this.bookingData.childSeats = { regular: 0, booster: 0 };
        }
        if (seatId === 'regular') {
            this.bookingData.childSeats.regular = quantity;
        } else {
            this.bookingData.childSeats.booster = quantity;
        }
        localStorage.setItem('shuttle_booking_data', JSON.stringify(this.bookingData));
        
        // Track GA event: child_seat_choosen (Row 14)
        if (window.ShuttleGAEvents) {
            const gaEvents = new window.ShuttleGAEvents();
            gaEvents.trackChildSeatChosen(
                this.bookingData.childSeats.regular,
                this.bookingData.childSeats.booster
            );
        }
        
        // Update UI
        const input = jQuery(`.shuttle-checkout__child-seat-quantity-input[data-seat-id="${seatId}"]`);
        if (input.length > 0) {
            input.val(quantity);
        }
        
        // Update button states
        const decreaseBtn = jQuery(`.shuttle-checkout__child-seat-quantity-btn[data-seat-id="${seatId}"][data-action="decrease"]`);
        if (decreaseBtn.length > 0) {
            decreaseBtn.prop('disabled', quantity === 0);
        }
        
        // Update booking summary
        this.updateBookingSummary();
    }
    
    showChildSeatTooltip(iconElement) {
        // Use .attr() instead of .data() to get fresh value (jQuery caches .data() values)
        const tooltipText = jQuery(iconElement).attr('data-tooltip');
        const existingTooltip = jQuery('.shuttle-checkout__child-seat-tooltip');
        
        if (existingTooltip.length) {
            existingTooltip.remove();
        }
        
        const tooltipHtml = `
            <div class="shuttle-checkout__child-seat-tooltip show">
                <div class="shuttle-checkout__child-seat-tooltip-content">${tooltipText}</div>
            </div>
        `;
        
        jQuery(iconElement).append(tooltipHtml);
    }
    
    updateBookingSummary() {
        this.renderSummaryStructure();
        this.updateTotal();
    }
    
    renderSummaryStructure() {
        const selectedRoutes = this.bookingData.selectedRoutes;
        const hasReturn = selectedRoutes.return;
        
        // Update section visibility and content based on trip type
        if (hasReturn) {
            // Return trip - show both sections
            jQuery('#summary-outbound-section').show();
            jQuery('#summary-return-section').show();
            
            // Update titles
            jQuery('[data-trip-title="outbound"]').text('Outbound');
            
            // Update content for both sections
            this.updateTripSection('outbound', selectedRoutes.outbound);
            this.updateTripSection('return', selectedRoutes.return);
        } else {
            // One-way trip - show only outbound section
            jQuery('#summary-outbound-section').show();
            jQuery('#summary-return-section').hide();
            
            // Update title for one-way
            jQuery('[data-trip-title="outbound"]').text('One-Way Transfer');
            
            // Update content for outbound section
            this.updateTripSection('outbound', selectedRoutes.outbound);
        }
    }
    
    updateTripSection(tripType, routeData) {
        if (!routeData) return;
        
        // Get route details - different logic for return trips
        let fromLabel, toLabel, routeText;
        
        if (tripType === 'return') {
            // Return trip: destination → origin (reversed)
            fromLabel = this.getLocationLabel(this.bookingData.to);
            toLabel = this.getLocationLabel(this.bookingData.from);
            routeText = `${fromLabel} → ${toLabel}`;
        } else {
            // Outbound or one-way: origin → destination
            fromLabel = this.getLocationLabel(this.bookingData.from);
            toLabel = this.getLocationLabel(this.bookingData.to);
            routeText = `${fromLabel} → ${toLabel}`;
        }
        
        // Format date and time based on trip type
        const dateText = this.formatDateTimeForSummary(tripType);
        
        // Update route text
        jQuery(`[data-route-text="${tripType}"]`).text(routeText);
        
        // Update date text
        jQuery(`[data-date-text="${tripType}"]`).text(dateText);
        
        // Generate and update line items
        this.updateLineItems(tripType);
    }
    
    updateLineItems(tripType) {
        const lineItemsContainer = jQuery(`[data-line-items="${tripType}"]`);
        const items = this.generateLineItemsData(tripType);
        
        // Clear existing items
        lineItemsContainer.empty();
        
        // Create line items using template
        items.forEach(item => {
            const template = jQuery('#line-item-template').html();
            const itemElement = jQuery(template);
            
            itemElement.find('[data-item-name]').text(item.name);
            itemElement.find('[data-item-quantity]').text(item.quantity);
            itemElement.find('[data-item-price]').text(item.price);
            
            lineItemsContainer.append(itemElement);
        });
    }
    
    generateLineItemsData(tripType) {
        const passengers = this.bookingData.passengers;
        const totalPassengers = (passengers.adults || 0) + (passengers.children || 0);
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);
        
        let items = [];
        
        // 1. Seats
        const seatsFare = this.calculateSeatsFareForTripType(tripType);
        const seatsTotal = seatsFare * totalPassengers;
        items.push({
            name: this.getTranslationString('seats'),
            quantity: `x${totalPassengers}`,
            price: `${currencySymbol}${seatsTotal.toFixed(2)}`
        });
        
        // 2. Fare condition (if not basic)
        const fareCondition = this.checkoutData.fareCondition || 'basic';
        if (fareCondition !== 'basic') {
            const fareUpgradePerWay = this.calculateFareUpgradePerWay(tripType);
            const fareName = fareCondition === 'skier' ? this.getTranslationString('alps2alps-skier') : this.getTranslationString('alps2alps-pro');
            items.push({
                name: fareName,
                quantity: `x${totalPassengers}`,
                price: fareUpgradePerWay > 0 ? `${currencySymbol}${fareUpgradePerWay.toFixed(2)}` : `${currencySymbol}0.00`
            });
        }
        
        // 2b. Return discount (admin-configured % off transfer + tier price, return leg only)
        if (tripType === 'return') {
            const discountEUR = this.calculateReturnDiscountEUR('return');
            if (discountEUR > 0) {
                const discountConverted = this.convertAndRoundPrice(discountEUR, 'EUR', currency);
                items.push({
                    name: this.getTranslationString('return-discount'),
                    quantity: '',
                    price: `-${currencySymbol}${discountConverted.toFixed(2)}`
                });
            }
        }
        
        // 3. Additional luggage (per-trip pricing)
        if (this.checkoutData.luggage.additional_bag > 0) {
            const additionalBagPriceEUR = this.extrasData.luggage?.price || 20;
            const additionalBagPriceConverted = this.convertAndRoundPrice(additionalBagPriceEUR, 'EUR', currency);
            const additionalBagTotal = this.checkoutData.luggage.additional_bag * additionalBagPriceConverted;
            items.push({
                name: this.getTranslationString('additional-luggage'),
                quantity: `x${this.checkoutData.luggage.additional_bag}`,
                price: `${currencySymbol}${additionalBagTotal.toFixed(2)}`
            });
        }
        
        // 4. Additional ski bags (per-trip pricing)
        if (this.checkoutData.luggage.ski_bag > 0) {
            const skiBagPriceEUR = this.extrasData.ski?.price || 20;
            const skiBagPriceConverted = this.convertAndRoundPrice(skiBagPriceEUR, 'EUR', currency);
            const skiBagTotal = this.checkoutData.luggage.ski_bag * skiBagPriceConverted;
            items.push({
                name: this.getTranslationString('additional-ski-bags'),
                quantity: `x${this.checkoutData.luggage.ski_bag}`,
                price: `${currencySymbol}${skiBagTotal.toFixed(2)}`
            });
        }
        
        // 5. Child seats (per-trip pricing)
        if (this.checkoutData.childSeats.regular > 0) {
            const childSeatPriceEUR = this.extrasData.child_seat?.price || 2;
            const childSeatPriceConverted = this.convertAndRoundPrice(childSeatPriceEUR, 'EUR', currency);
            const childSeatTotal = this.checkoutData.childSeats.regular * childSeatPriceConverted;
            items.push({
                name: this.getTranslationString('child-seat'),
                quantity: `x${this.checkoutData.childSeats.regular}`,
                price: `${currencySymbol}${childSeatTotal.toFixed(2)}`
            });
        }
        
        // 6. Child booster seats (per-trip pricing)
        if (this.checkoutData.childSeats.booster > 0) {
            const boosterSeatPriceEUR = this.extrasData.booster_seat?.price || 2;
            const boosterSeatPriceConverted = this.convertAndRoundPrice(boosterSeatPriceEUR, 'EUR', currency);
            const boosterSeatTotal = this.checkoutData.childSeats.booster * boosterSeatPriceConverted;
            items.push({
                name: this.getTranslationString('child-booster-seat'),
                quantity: `x${this.checkoutData.childSeats.booster}`,
                price: `${currencySymbol}${boosterSeatTotal.toFixed(2)}`
            });
        }
        
        // Return items data for template processing
        return items;
    }
    
    getLocationLabel(locationObj) {
        return locationObj?.label || locationObj?.id || 'Unknown';
    }
    
    formatDateTimeForSummary(tripType) {
        const selectedRoutes = this.bookingData.selectedRoutes;
        let routeData;
        
        if (tripType === 'return' && selectedRoutes.return) {
            // Use return route data
            routeData = selectedRoutes.return;
        } else if (selectedRoutes.outbound) {
            // Use outbound route data (for both outbound and one-way)
            routeData = selectedRoutes.outbound;
        }
        
        if (routeData && routeData.routeData && routeData.routeData.date && routeData.departureTime) {
            // Parse the date from routeData.date (format: "26.09.25, 15:30")
            const dateStr = routeData.routeData.date.split(',')[0]; // Get "26.09.25"
            const departureTime = routeData.departureTime; // Get actual departure time like "16:00"
            
            // Parse DD.MM.YY format
            const [day, month, year] = dateStr.split('.');
            const fullYear = 2000 + parseInt(year); // Convert 25 to 2025
            
            // Create date object
            const dateObj = new Date(fullYear, parseInt(month) - 1, parseInt(day));
            
            // Format to "Day, DD MMM YYYY, HH:MM"
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            
            const dayName = dayNames[dateObj.getDay()];
            const dayNum = dateObj.getDate();
            const monthName = monthNames[dateObj.getMonth()];
            const yearNum = dateObj.getFullYear();
            
            return `${dayName}, ${dayNum} ${monthName} ${yearNum}, ${departureTime}`;
        }
        
        return 'Date not selected';
    }
    
    updateTripType() {
        const hasReturn = this.bookingData.selectedRoutes.return;
        const tripType = hasReturn ? 'Return Transfer' : 'One-Way Transfer';
        jQuery('#trip-type-display').text(tripType);
    }
    
    updateRoutesSummary() {
        const container = jQuery('#routes-summary');
        const selectedRoutes = this.bookingData.selectedRoutes;
        let html = '';
        
        // Outbound route
        if (selectedRoutes.outbound) {
            const route = selectedRoutes.outbound;
            html += `
                <div class="shuttle-checkout-summary__route">
                    <div class="shuttle-checkout-summary__route-info">
                        <div class="shuttle-checkout-summary__route-name">${route.from} → ${route.to}</div>
                        <div class="shuttle-checkout-summary__route-date">${route.date}, ${route.time}</div>
                    </div>
                    <div class="shuttle-checkout-summary__route-price">${route.price.currency}${route.price.amount}</div>
                </div>
            `;
        }
        
        // Return route
        if (selectedRoutes.return) {
            const route = selectedRoutes.return;
            html += `
                <div class="shuttle-checkout-summary__route">
                    <div class="shuttle-checkout-summary__route-info">
                        <div class="shuttle-checkout-summary__route-name">${route.from} → ${route.to}</div>
                        <div class="shuttle-checkout-summary__route-date">${route.date}, ${route.time}</div>
                    </div>
                    <div class="shuttle-checkout-summary__route-price">${route.price.currency}${route.price.amount}</div>
                </div>
            `;
        }
        
        container.html(html);
    }
    
    updatePassengersSummary() {
        const passengers = this.bookingData.passengers;
        const adults = passengers.adults || 0;
        const children = passengers.children || 0;
        
        let passengerText = '';
        if (adults > 0) {
            passengerText += `${adults} Adult${adults > 1 ? 's' : ''}`;
        }
        if (children > 0) {
            if (passengerText) passengerText += ', ';
            passengerText += `${children} Child${children > 1 ? 'ren' : ''}`;
        }
        
        jQuery('#passengers-summary').html(`
            <p class="shuttle-checkout-summary__passengers-info">${passengerText}</p>
        `);
    }
    
    updatePriceBreakdown() {
        // Calculate all pricing components
        const baseFare = this.calculateBaseFare();
        const fareUpgrade = this.calculateFareUpgrade();
        const luggageTotal = this.calculateLuggageTotal();
        const childSeatsTotal = this.calculateChildSeatsTotal();
        
        // Update checkout data
        this.checkoutData.pricing.baseFare = baseFare;
        this.checkoutData.pricing.fareUpgrade = fareUpgrade;
        this.checkoutData.pricing.luggage = luggageTotal;
        this.checkoutData.pricing.childSeats = childSeatsTotal;
        
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);
        
        let breakdownHtml = `
            <div class="shuttle-checkout-summary__breakdown-item">
                <span class="shuttle-checkout-summary__breakdown-label">Base transfer fare</span>
                <span class="shuttle-checkout-summary__breakdown-amount">${currencySymbol}${baseFare.toFixed(2)}</span>
            </div>
        `;
        
        // Add fare upgrade if applicable
        if (fareUpgrade > 0) {
            const fareConditionName = this.getFareConditionName(this.checkoutData.fareCondition);
            breakdownHtml += `
                <div class="shuttle-checkout-summary__breakdown-item">
                    <span class="shuttle-checkout-summary__breakdown-label">${fareConditionName} upgrade</span>
                    <span class="shuttle-checkout-summary__breakdown-amount">${currencySymbol}${fareUpgrade.toFixed(2)}</span>
                </div>
            `;
        }
        
        // Add luggage if applicable (placeholder)
        if (luggageTotal > 0) {
            breakdownHtml += `
                <div class="shuttle-checkout-summary__breakdown-item">
                    <span class="shuttle-checkout-summary__breakdown-label">Additional luggage</span>
                    <span class="shuttle-checkout-summary__breakdown-amount">${currencySymbol}${luggageTotal.toFixed(2)}</span>
                </div>
            `;
        }
        
        // Add child seats if applicable (placeholder)
        if (childSeatsTotal > 0) {
            breakdownHtml += `
                <div class="shuttle-checkout-summary__breakdown-item">
                    <span class="shuttle-checkout-summary__breakdown-label">Child seats</span>
                    <span class="shuttle-checkout-summary__breakdown-amount">${currencySymbol}${childSeatsTotal.toFixed(2)}</span>
                </div>
            `;
        }
        
        jQuery('#price-breakdown').html(breakdownHtml);
    }
    
    calculateFareUpgrade() {
        // Sum the real per-route tier delta for each transfer way separately, since
        // outbound and return can be different routes with different real tier prices.
        let total = this.calculateFareUpgradePerWay('outbound');
        
        if (this.bookingData.selectedRoutes.return) {
            total += this.calculateFareUpgradePerWay('return');
        }
        
        // Round the final total
        return Math.round(total * 100) / 100;
    }
    
    calculateFareUpgradePerWay(direction = 'outbound') {
        const fareCondition = this.checkoutData.fareCondition;
        const passengers = this.bookingData.passengers;
        const totalPassengers = (passengers.adults || 0) + (passengers.children || 0);
        const currency = this.bookingData.currency || 'EUR';
        
        // Get the real per-route fare-tier upgrade delta (per passenger, in EUR)
        const route = this.bookingData.selectedRoutes[direction];
        const upgradePerPassengerEUR = this.getTierDeltaEUR(route, fareCondition);
        
        // Convert the per-passenger price to current currency
        const upgradePerPassengerConverted = this.convertAndRoundPrice(upgradePerPassengerEUR, 'EUR', currency);
        
        // Calculate for one transfer way only
        const total = upgradePerPassengerConverted * totalPassengers;
        
        return Math.round(total * 100) / 100;
    }
    
    getFareConditionName(fareId) {
        const fareNames = {
            'basic': 'Alps2Alps Basic',
            'skier': 'Alps2Alps Skier',
            'pro': 'Alps2Alps Pro'
        };
        return fareNames[fareId] || 'Unknown';
    }
    
    calculateLuggageTotal() {
        const luggage = this.checkoutData.luggage;
        const currency = this.bookingData.currency || 'EUR';
        let total = 0;
        
        // Additional bags - use converted price
        if (luggage.additional_bag > 0) {
            const priceInEUR = this.extrasData.luggage?.price || 20;
            const convertedPrice = this.convertAndRoundPrice(priceInEUR, 'EUR', currency);
            total += luggage.additional_bag * convertedPrice;
        }
        
        // Ski bags - use converted price
        if (luggage.ski_bag > 0) {
            const priceInEUR = this.extrasData.ski?.price || 20;
            const convertedPrice = this.convertAndRoundPrice(priceInEUR, 'EUR', currency);
            total += luggage.ski_bag * convertedPrice;
        }
        
        // Double for return trips
        if (this.bookingData.selectedRoutes.return) {
            total *= 2;
        }
        
        // Round the final total
        return Math.round(total * 100) / 100;
    }
    
    calculateChildSeatsTotal() {
        const childSeats = this.checkoutData.childSeats;
        const currency = this.bookingData.currency || 'EUR';
        let total = 0;
        
        // Regular child seats - use converted price
        if (childSeats.regular > 0) {
            const priceInEUR = this.extrasData.child_seat?.price || 5;
            const convertedPrice = this.convertAndRoundPrice(priceInEUR, 'EUR', currency);
            total += childSeats.regular * convertedPrice;
        }
        
        // Booster seats - use converted price
        if (childSeats.booster > 0) {
            const priceInEUR = this.extrasData.booster_seat?.price || 5;
            const convertedPrice = this.convertAndRoundPrice(priceInEUR, 'EUR', currency);
            total += childSeats.booster * convertedPrice;
        }
        
        // Double for return trips
        if (this.bookingData.selectedRoutes.return) {
            total *= 2;
        }
        
        // Round the final total
        return Math.round(total * 100) / 100;
    }
    
    calculateBaseFare() {
        const selectedRoutes = this.bookingData.selectedRoutes;
        const passengers = this.bookingData.passengers;
        const totalPassengers = (passengers.adults || 0) + (passengers.children || 0);
        
        let total = 0;
        
        // Outbound fare
        if (selectedRoutes.outbound) {
            total += selectedRoutes.outbound.price.amount * totalPassengers;
        }
        
        // Return fare
        if (selectedRoutes.return) {
            total += selectedRoutes.return.price.amount * totalPassengers;
        }
        
        return total;
    }
    
    updateTotal() {
        // Calculate all pricing components fresh using individual converted prices
        const passengers = this.bookingData.passengers;
        const totalPassengers = (passengers.adults || 0) + (passengers.children || 0);
        
        // Calculate base fare by summing individual converted route prices (matches summary display)
        const baseFare = this.calculateBaseFareFromConvertedRoutes(totalPassengers);
        
        const fareUpgrade = this.calculateFareUpgrade();
        const luggageTotal = this.calculateLuggageTotal();
        const childSeatsTotal = this.calculateChildSeatsTotal();
        
        // 20% return-leg discount, converted to the display currency (matches the
        // return discount line item in the summary)
        const currency = this.bookingData.currency || 'EUR';
        const returnDiscount = this.convertAndRoundPrice(this.calculateReturnDiscountEUR('return'), 'EUR', currency);
        
        // Update checkout data pricing
        this.checkoutData.pricing.baseFare = baseFare;
        this.checkoutData.pricing.fareUpgrade = fareUpgrade;
        this.checkoutData.pricing.luggage = luggageTotal;
        this.checkoutData.pricing.childSeats = childSeatsTotal;
        this.checkoutData.pricing.returnDiscount = returnDiscount;
        
        // Calculate total by summing the already-converted components (minus return discount)
        const total = Math.round((baseFare + fareUpgrade + luggageTotal + childSeatsTotal - returnDiscount) * 100) / 100;
        this.checkoutData.pricing.total = total;
        
        // Update display
        const currencySymbol = this.getCurrencySymbol(currency);
        jQuery('#total-amount').text(`${currencySymbol}${total.toFixed(2)}`);
    }
    
    getCurrencySymbol(currency) {
        // Use currency manager's symbol method for consistency
        return this.currencyManager.getCurrencySymbol(currency);
    }
    
    /**
     * Convert and round price to avoid floating point precision issues
     */
    convertAndRoundPrice(amount, fromCurrency = 'EUR', toCurrency = null) {
        const convertedPrice = this.currencyManager.convertPrice(amount, fromCurrency, toCurrency);
        return Math.round(convertedPrice * 100) / 100;
    }
    
    /**
     * Format price - remove .00 for round numbers, keep decimals when needed
     */
    formatPrice(price) {
        // Remove .00 for round numbers, keep decimals when needed
        return price === Math.floor(price) ? price.toString() : price.toFixed(2);
    }
    
    setupEventListeners() {
        // Continue to payment button
        jQuery('#continue-to-payment').on('click', (e) => {
            e.preventDefault();
            this.handleContinueToPayment();
        });
        
        // Additional info textarea
        jQuery('#additional-info').on('input', (e) => {
            this.checkoutData.additionalInfo = jQuery(e.target).val();
        });
        
        // Setup auto-save for form fields
        this.setupFormAutoSave();
    }
    
    /**
     * Set up currency change listener (inherited from parent but needs to be called)
     */
    setupCurrencyListener() {
        // Listen for currency change events from the header currency switcher
        window.addEventListener('shuttleCurrencyChanged', async (event) => {
            await this.handleCurrencyChange(event.detail);
        });
    }
    
    /**
     * Override parent method to handle checkout-specific currency refresh
     */
    async refreshCurrentView() {
        try {
            // Update booking data currency
            const currentCurrency = this.currencyManager.getCurrentCurrency();
            this.bookingData.currency = currentCurrency.code;
            this.saveBookingData(this.bookingData);
            
            // Convert and update all prices in the checkout
            await this.refreshCheckoutPrices();
            
            // Update the booking summary with new currency
            this.updateBookingSummary();
            
        } catch (error) {
            console.error('Failed to refresh checkout view with new currency:', error);
        }
    }
    
    /**
     * Refresh all prices in checkout with new currency
     */
    async refreshCheckoutPrices() {
        try {
            // Update prices without re-initializing sections (to avoid duplicate event listeners)
            this.updateFareConditionPrices();
            this.updateLuggagePrices();
            this.updateChildSeatPrices();
            
            // Update any other price displays
            this.updateTotal();
            
        } catch (error) {
            console.error('Failed to refresh checkout prices:', error);
        }
    }
    
    /**
     * Update fare condition prices without re-initializing the section
     */
    updateFareConditionPrices() {
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);

        // Use the outbound route's real per-tier prices as the representative
        // per-passenger upgrade cost shown on the fare cards. getTierDeltaEUR() falls
        // back to the legacy hardcoded delta if real tier data is unavailable.
        const outboundRoute = this.bookingData.selectedRoutes?.outbound || null;

        // Update fare option prices
        jQuery('.shuttle-checkout__fare-option').each((index, element) => {
            const fareElement = jQuery(element);
            const fareId = fareElement.data('fare-id');

            // Real per-route delta over basic (per passenger, EUR)
            const basePriceEUR = this.getTierDeltaEUR(outboundRoute, fareId);

            // Convert price from EUR to current currency
            const convertedPrice = this.convertAndRoundPrice(basePriceEUR, 'EUR', currency);

            // Update price display
            fareElement.find('.currency-symbol').text(currencySymbol);
            fareElement.find('.price-amount').text(convertedPrice.toFixed(2));
        });
    }
    
    /**
     * Update luggage prices without re-initializing the section
     */
    updateLuggagePrices() {
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);
        const hasReturn = this.bookingData.selectedRoutes.return;
        
        // Update luggage option prices
        jQuery('.shuttle-checkout__luggage-option').each((index, element) => {
            const luggageId = jQuery(element).data('luggage-id');
            let priceEUR = 20; // Default price
            
            if (luggageId === 'additional_bag') {
                priceEUR = this.extrasData.luggage?.price || 20;
            } else if (luggageId === 'ski_bag') {
                priceEUR = this.extrasData.ski?.price || 20;
            }
            
            const convertedPrice = this.convertAndRoundPrice(priceEUR, 'EUR', currency);
            const displayPrice = hasReturn ? convertedPrice * 2 : convertedPrice;
            const oneWayPrice = convertedPrice; // For tooltip
            
            // Update price amount (not the entire price container)
            const priceContainer = jQuery(element).find('.shuttle-checkout__luggage-option-price');
            priceContainer.find('.currency-symbol').text(currencySymbol);
            priceContainer.find('.price-amount')
                .attr('data-base-price', priceEUR)
                .text(this.formatPrice(displayPrice));
            
            // Update tooltip with one-way price
            const tooltip = priceContainer.find('.shuttle-checkout__luggage-option-info-icon');
            tooltip.attr('data-tooltip-base', priceEUR);
            tooltip.attr('data-tooltip', `${currencySymbol}${this.formatPrice(oneWayPrice)}/${this.getTranslationString('one-way')}`);
            
            // Update trip type text
            const tripTypeText = hasReturn ? this.getTranslationString('return-trip') : this.getTranslationString('one-way');
            jQuery(element).find('.shuttle-checkout__luggage-option-trip-type').text(tripTypeText);
        });
    }
    
    /**
     * Update child seat prices without re-initializing the section
     */
    updateChildSeatPrices() {
        const currency = this.bookingData.currency || 'EUR';
        const currencySymbol = this.getCurrencySymbol(currency);
        const hasReturn = this.bookingData.selectedRoutes.return;
        
        // Update child seat option prices
        jQuery('.shuttle-checkout__child-seat-option').each((index, element) => {
            const seatId = jQuery(element).data('seat-id');
            let priceEUR = 2; // Default price
            
            if (seatId === 'regular') {
                priceEUR = this.extrasData.child_seat?.price || 2;
            } else if (seatId === 'booster') {
                priceEUR = this.extrasData.booster_seat?.price || 2;
            }
            
            const convertedPrice = this.convertAndRoundPrice(priceEUR, 'EUR', currency);
            const displayPrice = hasReturn ? convertedPrice * 2 : convertedPrice;
            const oneWayPrice = convertedPrice; // For tooltip
            
            // Update price amount (not the entire price container)
            const priceContainer = jQuery(element).find('.shuttle-checkout__child-seat-option-price');
            priceContainer.find('.currency-symbol').text(currencySymbol);
            priceContainer.find('.price-amount')
                .attr('data-base-price', priceEUR)
                .text(this.formatPrice(displayPrice));
            
            // Update tooltip with one-way price
            const tooltip = priceContainer.find('.shuttle-checkout__child-seat-option-info-icon');
            tooltip.attr('data-tooltip-base', priceEUR);
            tooltip.attr('data-tooltip', `${currencySymbol}${this.formatPrice(oneWayPrice)}/${this.getTranslationString('one-way')}`);
            
            // Update trip type text
            const tripTypeText = hasReturn ? this.getTranslationString('return-trip') : this.getTranslationString('one-way');
            jQuery(element).find('.shuttle-checkout__child-seat-option-trip-type').text(tripTypeText);
        });
    }
    
    handleContinueToPayment() {
        // Get form data
        const formData = this.formValidator.getFormData();
        
        // Validate form
        const validation = this.formValidator.validateForm(formData);
        
        if (!validation.isValid) {
            this.formValidator.showFormErrors(validation.errors);
            return;
        }
        
        // Additional phone validation - check if phone number is valid for selected country
        if (this.phoneInputInstance) {
            const phoneNumber = this.phoneInputInstance.getPhoneNumber();
            const phoneWithoutDialCode = this.phoneInputInstance.getPhoneNumberWithoutDialCode();
            
            // Check if phone number is empty or too short
            if (!phoneWithoutDialCode || phoneWithoutDialCode.length < 8) {
                const phoneInput = jQuery('#passenger-phone');
                const phoneError = jQuery('#phone-error');
                
                phoneInput.addClass('shuttle-checkout__input--error');
                phoneError.addClass('shuttle-checkout__field-error--visible')
                    .text(this.getTranslatedString('phoneInvalid') || 'Please enter a valid phone number');
                
                // Scroll to phone field
                jQuery('html, body').animate({
                    scrollTop: phoneInput.offset().top - 100
                }, 300);
                
                return;
            }
        }
        
        // Update checkout data
        this.checkoutData.leadPassenger = formData;
        
        // Save checkout data to localStorage for thank you page
        try {
            localStorage.setItem('shuttle_checkout_data', JSON.stringify(this.checkoutData));
        } catch (e) {
            console.error('[ShuttleCheckout] Failed to save checkout data:', e);
        }
        
        // Validate bus stop selection (only if bus stops are available)
        if (this.shouldShowBusStops && !this.checkoutData.busStop) {
            this.showError(this.getTranslatedString('busStopRequired'));
            return;
        }
        
        // IMPORTANT: Check if password is required but not filled
        const passwordField = jQuery('#passenger-password');
        const emailField = jQuery('#passenger-email');
        const password = passwordField.val();
        
        // If password field is visible, it means the email exists and user needs to log in
        if (passwordField.is(':visible') && (!password || password.trim() === '')) {
            this.showError(this.getTranslatedString('passwordRequired'));
            
            // Scroll to and highlight the password field
            passwordField.focus();
            passwordField.addClass('shuttle-checkout__input--error');
            
            // Add error message under password field if not already present
            if (!passwordField.next('.shuttle-checkout__error-message').length) {
                passwordField.after('<span class="shuttle-checkout__error-message">Password is required to log in</span>');
            }
            
            return;
        }
        
        // Track GA event: pay_clicked (Row 15)
        if (window.ShuttleGAEvents) {
            const gaEvents = new window.ShuttleGAEvents();
            gaEvents.trackPayClicked(this.checkoutData);
        }
        
        // Check if user is logged in or needs to register
        this.processAccountCreation();
    }
    
    /**
     * Process booking submission
     * Account creation is now handled by the /book API endpoint
     */
    async processAccountCreation() {
        // Simply proceed to create booking
        // The /book API will handle account creation if user_key is not provided
        await this.createBooking();
    }
    
    /**
     * Handle user login (existing user with password)
     * Uses form submission (not AJAX) to avoid CORS issues
     */
    handleUserLogin() {
        const formData = this.formValidator.getFormData();
        const email = formData.email;
        const password = jQuery('#passenger-password').val();
        
        if (!password || password.trim() === '') {
            this.showError(this.getTranslatedString('passwordEmpty'));
            return;
        }
        
        try {
            // Get current pathname with language prefix for redirect after login
            // Use pathname (e.g., "/fr/shuttle-booking/shuttle-checkout/") not full URL
            const currentPathname = window.location.pathname;
            
            // Create hidden form for login submission (backend expects form submission, not AJAX)
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = a2a_get_api_base_url() + '/loyalty/booking-auth?source=shuttle-booking';
            form.style.display = 'none';
            
            // Add email field - backend expects Profile[email] format
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'Profile[email]';
            emailInput.value = email;
            form.appendChild(emailInput);
            
            // Add password field - backend expects Profile[password] format
            const passwordInput = document.createElement('input');
            passwordInput.type = 'hidden';
            passwordInput.name = 'Profile[password]';
            passwordInput.value = password;
            form.appendChild(passwordInput);
            
            // Add redirect URL with language prefix (pathname only, backend adds base URL)
            const redirectInput = document.createElement('input');
            redirectInput.type = 'hidden';
            redirectInput.name = 'redirect_url';
            redirectInput.value = currentPathname; // Send pathname like "/fr/shuttle-booking/shuttle-checkout/"
            form.appendChild(redirectInput);
            
            // Submit form (will trigger browser reload)
            document.body.appendChild(form);
            form.submit();
            
            // Execution stops here - browser reloads
            // After redirect, page will be: /fr/shuttle-checkout/?auth={...} (with language prefix!)
            // shuttle-user-auth.js will handle the redirect with handleAuthRedirect()
            
        } catch (error) {
            console.error('Login form submission failed:', error);
            this.showError(this.getTranslatedString('loginFailed'));
        }
    }
    
    /**
     * Handle user registration (new user without password)
     * Uses form submission (not AJAX) to avoid CORS issues - same as login
     */
    handleUserRegistration() {
        const formData = this.formValidator.getFormData();
        
        // Get phone input instance and country code ID
        const phoneInput = this.phoneInputInstance;
        if (!phoneInput) {
            this.showError(this.getTranslatedString('phoneInputError'));
            return;
        }
        
        const countryCodeId = phoneInput.getCountryCodeId();
        
        if (!countryCodeId) {
            this.showError(this.getTranslatedString('countryCodeRequired'));
            return;
        }
        
        // Extract phone number WITHOUT country code
        const fullPhoneNumber = phoneInput.getPhoneNumber();
        const selectedCountry = phoneInput.getSelectedCountry();
        const dialCode = selectedCountry ? selectedCountry.dialCode : '';
        
        // Remove dial code and all spaces to get just the number
        let phoneNumberOnly = fullPhoneNumber;
        if (dialCode) {
            phoneNumberOnly = phoneNumberOnly.replace(dialCode, '').trim();
        }
        phoneNumberOnly = phoneNumberOnly.replace(/\s+/g, ''); // Remove all spaces
        
        // Build registration payload
        const registrationData = {
            email: formData.email,
            // NO PASSWORD for registration!
            Profile: {
                name: formData.firstName,
                surname: formData.lastName,
                country_code_id_1: countryCodeId,
                phone_number_1: phoneNumberOnly,
                is_agree_terms_and_conditions: 1 // Backend expects integer, not boolean
            }
        };
        
        try {
            // Get current pathname with language prefix for redirect after registration
            // Use pathname (e.g., "/fr/shuttle-booking/shuttle-checkout/") not full URL
            const currentPathname = window.location.pathname;
            
            // Create hidden form for registration submission (backend expects form submission, not AJAX)
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = a2a_get_api_base_url() + '/loyalty/booking-auth?source=shuttle-booking';
            form.style.display = 'none';
            
            // Add email field - also goes under Profile[] for registration
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'Profile[email]';
            emailInput.value = registrationData.email;
            form.appendChild(emailInput);
            
            // Add all other Profile fields - backend expects Profile[...] format
            const profileFields = registrationData.Profile;
            for (const [key, value] of Object.entries(profileFields)) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `Profile[${key}]`;
                input.value = value;
                form.appendChild(input);
            }
            
            // Add redirect URL with language prefix (pathname only, backend adds base URL)
            const redirectInput = document.createElement('input');
            redirectInput.type = 'hidden';
            redirectInput.name = 'redirect_url';
            redirectInput.value = currentPathname; // Send pathname like "/fr/shuttle-booking/shuttle-checkout/"
            form.appendChild(redirectInput);
            
            // Submit form (will trigger browser reload)
            document.body.appendChild(form);
            form.submit();
            
            // Execution stops here - browser reloads
            // After redirect, page will be: /fr/shuttle-checkout/?auth={...} (with language prefix!)
            // shuttle-user-auth.js will handle the redirect with handleAuthRedirect()
            
        } catch (error) {
            console.error('Registration form submission failed:', error);
            this.showError(this.getTranslatedString('registrationFailed'));
        }
    }
    
    showSuccess(message) {
        // Create or update success message
        let successDiv = jQuery('.shuttle-checkout__success-message');
        if (successDiv.length === 0) {
            successDiv = jQuery('<div class="shuttle-checkout__success-message"></div>');
            jQuery('.shuttle-checkout__form-column').prepend(successDiv);
        }
        
        successDiv.html(`
            <div style="background: #F0FDF4; border: 1px solid #86EFAC; border-radius: 8px; padding: 16px; margin-bottom: 16px; color: #166534;">
                <strong>Success:</strong> ${message}
            </div>
        `);
        
        // Scroll to success message
        jQuery('html, body').animate({
            scrollTop: successDiv.offset().top - 100
        }, 300);
    }
    
    showError(message) {
        // Create or update error message
        let errorDiv = jQuery('.shuttle-checkout__error-message');
        if (errorDiv.length === 0) {
            errorDiv = jQuery('<div class="shuttle-checkout__error-message"></div>');
            jQuery('.shuttle-checkout__form-column').prepend(errorDiv);
        }
        
        errorDiv.html(`
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 8px; padding: 16px; margin-bottom: 16px; color: #DC2626;">
                <strong></strong> <span></span>
            </div>
        `);
        errorDiv.find('strong').text(this.getTranslatedString('errorPrefix'));
        errorDiv.find('span').text(message);
        
        // Scroll to error
        jQuery('html, body').animate({
            scrollTop: errorDiv.offset().top - 100
        }, 300);
    }
    
    /**
     * Create booking order via WordPress AJAX using Secure API (HMAC)
     * Server-to-server authentication, no user token needed
     */
    async createBooking() {
        try {
            // Show loading state
            this.showLoadingState();
            
            // Capture additional info from textarea before building payload
            this.checkoutData.additionalInfo = jQuery('#additional-info').val() || null;
            
            // Build booking payload
            const bookingPayload = this.buildBookingPayload();
            
            // Prepare form data for WordPress AJAX
            const formData = new FormData();
            formData.append('action', 'shuttle_create_booking');
            formData.append('currency', bookingPayload.currency);
            
            // Add customer data
            for (const [key, value] of Object.entries(bookingPayload.customer)) {
                formData.append(`customer[${key}]`, value !== null ? value : '');
            }
            
            // Add transfers data
            if (bookingPayload.transfers.outbound) {
                for (const [key, value] of Object.entries(bookingPayload.transfers.outbound)) {
                    formData.append(`transfers[outbound][${key}]`, value !== null ? value : '');
                }
            }
            
            if (bookingPayload.transfers.return) {
                for (const [key, value] of Object.entries(bookingPayload.transfers.return)) {
                    formData.append(`transfers[return][${key}]`, value !== null ? value : '');
                }
            }
            
            // Make AJAX request to WordPress (same-origin, no CORS issues)
            const response = await fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            
            const result = await response.json();
            
            if (result.success) {
                this.handleBookingSuccess(result.data);
            } else {
                this.handleBookingError(result.data);
                this.hideLoadingState();
            }
            
        } catch (error) {
            this.hideLoadingState();
            this.showError(this.getTranslatedString('bookingSubmitFailed'));
        }
    }
    
    /**
     * Get user_key from _a2alu_test cookie
     * user_key is the encrypted user ID (the 'id' field in cookie data)
     */
    getUserKey() {
        try {
            // Determine cookie name based on environment
            const cookieName = window.location.hostname.includes('test') ? '_a2alu_test' : '_a2alu';
            
            // Parse cookies
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === cookieName) {
                    try {
                        const cookieData = JSON.parse(decodeURIComponent(value));
                        
                        // The user_key is the TOP-LEVEL 'id' field (encrypted user ID)
                        // NOT the 'id' inside the Base64 'data' field (which is numeric DB ID)
                        if (cookieData.id) {
                            return cookieData.id; // This is the encrypted user ID (e.g., "2b2O6F7C6I653Wry")
                        }
                    } catch (e) {
                        // Failed to parse cookie
                    }
                }
            }
        } catch (e) {
            // Error getting user_key
        }
        
        return null;
    }
    
    /**
     * Add form fields for nested data structure
     */
    addFormFields(form, data, prefix) {
        for (const [key, value] of Object.entries(data)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = `${prefix}[${key}]`;
            input.value = value !== null ? value : '';
            form.appendChild(input);
        }
    }
    
    /**
     * Build booking payload for API
     * Structure: { user_key?, currency, customer, transfers: { outbound, return? } }
     */
    buildBookingPayload() {
        const currency = this.bookingData.currency || 'EUR';
        
        // Get user_key if user is logged in
        const userKey = this.getUserKey();
        
        // Build customer data
        const customer = {
            name: this.checkoutData.leadPassenger.firstName || '',
            surname: this.checkoutData.leadPassenger.lastName || '',
            email: this.checkoutData.leadPassenger.email || '',
            country_code_id_1: this.checkoutData.leadPassenger.countryCodeId || null,
            mobile_number_1: this.checkoutData.leadPassenger.phone || '',
            country_code_id_2: null,
            mobile_number_2: '',
            is_subscribed: 0,
            is_agree_terms_and_conditions: 1,
            is_agree_to_phone: 0
        };
        
        // Build transfers data
        const transfers = {};
        
        // Build outbound transfer
        transfers.outbound = this.buildTransferData('outbound');
        
        // Build return transfer if applicable
        if (this.bookingData.transferType === 'return' && this.bookingData.selectedRoutes.return) {
            transfers.return = this.buildTransferData('return');
        }
        
        // Build payload
        const payload = {
            currency,
            customer,
            transfers
        };
        
        // Add user_key if available (logged in user)
        if (userKey) {
            payload.user_key = userKey;
        }
        
        return payload;
    }
    
    /**
     * Build transfer data for outbound or return
     */
    buildTransferData(direction) {
        const route = this.bookingData.selectedRoutes[direction];
        const isOutbound = direction === 'outbound';
        
        // Get date and time
        const dateStr = isOutbound ? this.bookingData.outboundDate : this.bookingData.returnDate;
        const timeStr = route.departureTime;
        
        // Format date from "06.10.25" to "2026-10-06"
        const formattedDate = this.formatDateForAPI(dateStr);
        
        // Format time from "09:50" to "09:50:00"
        const formattedTime = timeStr.includes(':') ? `${timeStr}:00` : timeStr;
        
        // Get price data
        const priceData = this.calculateTransferPrice(direction);
        
        // Get location codes (swap for return direction)
        const codeFrom = isOutbound ? this.bookingData.from.id : this.bookingData.to.id;
        const codeTo = isOutbound ? this.bookingData.to.id : this.bookingData.from.id;
        
        return {
            fare: this.checkoutData.fareCondition || 'basic',
            code_from: codeFrom,
            code_to: codeTo,
            stop_id: this.bookingData.selectedBusStop || null,
            pick_up_date: formattedDate,
            pick_up_time: formattedTime,
            adult_count: this.bookingData.passengers.adults || 0,
            children_count: this.bookingData.passengers.children || 0,
            extra_luggage_count: this.checkoutData.luggage.additional_bag || 0,
            extra_ski_count: this.checkoutData.luggage.ski_bag || 0,
            extra_booster_seat_count: this.checkoutData.childSeats.booster || 0,
            extra_child_seat_count: this.checkoutData.childSeats.regular || 0,
            additional_info: this.checkoutData.additionalInfo || null,
            price_eur: priceData.price_eur,
            price: priceData.price,
            // Always numeric (0 when no discount) so it is never sent empty
            discount_eur: priceData.discount_eur || 0
        };
    }
    
    /**
     * Format date from "06.10.25" to "2026-10-06"
     */
    formatDateForAPI(dateStr) {
        // Parse "06.10.25, 09:20" or "06.10.25" format
        const datePart = dateStr.split(',')[0].trim();
        const parts = datePart.split('.');
        
        if (parts.length !== 3) {
            console.error('Invalid date format:', dateStr);
            return dateStr;
        }
        
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        let year = parts[2];
        
        // Convert 2-digit year to 4-digit (25 -> 2025)
        if (year.length === 2) {
            year = '20' + year;
        }
        
        return `${year}-${month}-${day}`;
    }
    
    /**
     * Calculate the 20% return-leg discount in EUR.
     *
     * The discount applies only to the transfer + tier price (seats + real fare-tier
     * delta), NOT to extras. Returns 0 for outbound, one-way trips, or when there is
     * no return leg. This is the single source of truth for the discount value, used
     * by both calculateTransferPrice() (payload/totals) and the summary line item.
     *
     * @param {string} direction - 'outbound' | 'return'
     * @returns {number} Discount amount in EUR (rounded to 2 decimals), 0 if N/A
     */
    calculateReturnDiscountEUR(direction) {
        if (direction !== 'return' || !this.bookingData.selectedRoutes.return) {
            return 0;
        }
        
        const route = this.bookingData.selectedRoutes.return;
        const passengers = this.bookingData.passengers;
        const totalPassengers = (passengers.adults || 0) + (passengers.children || 0);
        
        // Base seat price in EUR (basic tier)
        let seatPriceEUR = route.price?.amount || 0;
        if (route.price?.currency && route.price.currency !== 'EUR') {
            seatPriceEUR = route.price.original?.amount || seatPriceEUR;
        }
        const seatsTotalEUR = seatPriceEUR * totalPassengers;
        
        // Real per-route tier delta in EUR
        const fareCondition = this.checkoutData.fareCondition || 'basic';
        const fareUpgradeTotalEUR = this.getTierDeltaEUR(route, fareCondition) * totalPassengers;
        
        // Admin-configured discount off transfer + tier price (defaults to 20%)
        const discountEUR = (seatsTotalEUR + fareUpgradeTotalEUR) * this.getReturnDiscountRate();
        return Math.round(discountEUR * 100) / 100;
    }
    
    /**
     * Calculate total transfer price (transfer + extras) in current currency and EUR
     */
    calculateTransferPrice(direction) {
        const route = this.bookingData.selectedRoutes[direction];
        const currency = this.currencyManager.currentCurrency;
        const passengers = this.bookingData.passengers;
        const totalPassengers = (passengers.adults || 0) + (passengers.children || 0);
        
        // Get base seat price from route (price per seat)
        let seatPriceEUR = route.price?.amount || 0;
        
        // If route price is already in different currency, convert back to EUR
        if (route.price?.currency && route.price.currency !== 'EUR') {
            // Use original EUR price if available
            seatPriceEUR = route.price.original?.amount || seatPriceEUR;
        }
        
        // Calculate seats total (seat price × passengers)
        const seatsTotalEUR = seatPriceEUR * totalPassengers;
        
        // Calculate fare upgrade (if not basic) using the real per-route tier delta
        const fareCondition = this.checkoutData.fareCondition || 'basic';
        const fareUpgradePerPassengerEUR = this.getTierDeltaEUR(route, fareCondition);
        const fareUpgradeTotalEUR = fareUpgradePerPassengerEUR * totalPassengers;
        
        // Calculate extras prices in EUR (just the quantities selected, NOT multiplied by passengers)
        let extrasPriceEUR = 0;
        
        // Luggage prices (user-selected quantities)
        const luggagePricePerBag = this.extrasData.luggage?.price || 20;
        const skiPricePerBag = this.extrasData.ski?.price || 20;
        extrasPriceEUR += (this.checkoutData.luggage.additional_bag || 0) * luggagePricePerBag;
        extrasPriceEUR += (this.checkoutData.luggage.ski_bag || 0) * skiPricePerBag;
        
        // Child seat prices (user-selected quantities)
        const regularSeatPrice = this.extrasData.child_seat?.price || 2;
        const boosterSeatPrice = this.extrasData.booster_seat?.price || 2;
        extrasPriceEUR += (this.checkoutData.childSeats.regular || 0) * regularSeatPrice;
        extrasPriceEUR += (this.checkoutData.childSeats.booster || 0) * boosterSeatPrice;
        
        // 20% return-leg discount on transfer + tier price (0 for outbound/one-way)
        const discountEUR = this.calculateReturnDiscountEUR(direction);
        
        // Total price in EUR for this direction: seats + fare upgrade + extras - discount
        const totalPriceEUR = seatsTotalEUR + fareUpgradeTotalEUR + extrasPriceEUR - discountEUR;
        
        // Convert to current currency
        let totalPriceInCurrency = totalPriceEUR;
        if (currency !== 'EUR') {
            totalPriceInCurrency = this.currencyManager.convertPrice(totalPriceEUR, 'EUR', currency);
        }
        
        return {
            price: Math.round(totalPriceInCurrency * 100) / 100,
            currency: currency,
            price_eur: Math.round(totalPriceEUR * 100) / 100,
            // EUR-pegged discount for backend validation; always numeric (0 when N/A)
            discount_eur: discountEUR
        };
    }
    
    /**
     * Show loading state
     */
    showLoadingState() {
        const button = jQuery('#continue-to-payment');
        button.prop('disabled', true);
        button.html('<span style="opacity: 0.6;">Processing...</span>');
        
        // Add loading overlay
        if (jQuery('.shuttle-checkout__loading-overlay').length === 0) {
            jQuery('.shuttle-checkout').append(`
                <div class="shuttle-checkout__loading-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.8); display: flex; align-items: center; justify-content: center; z-index: 9999;">
                    <div style="text-align: center;">
                        <div style="border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
                        <p style="font-size: 16px; color: #333;">Creating your booking...</p>
                    </div>
                </div>
                <style>
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                </style>
            `);
        }
    }
    
    /**
     * Hide loading state
     */
    hideLoadingState() {
        const button = jQuery('#continue-to-payment');
        button.prop('disabled', false);
        button.html('Continue to Payment');
        jQuery('.shuttle-checkout__loading-overlay').remove();
    }
    
    /**
     * Handle successful booking creation
     */
    handleBookingSuccess(data) {
        // Check if we have order data
        if (!data || typeof data !== 'object') {
            this.showSuccess(this.getTranslatedString('bookingSuccess'));
            this.hideLoadingState();
            return;
        }
        
        // Store order data in localStorage for payment page
        // Include calculated totals for accurate price display
        try {
            const orderDataWithTotals = {
                ...data,
                // Add calculated totals from the booking payload
                calculatedTotals: {
                    outbound: this.calculateTransferPrice('outbound'),
                    return: this.bookingData.transferType === 'return' ? this.calculateTransferPrice('return') : null
                }
            };
            localStorage.setItem('shuttle_order_data', JSON.stringify(orderDataWithTotals));
        } catch (e) {
            // Failed to store order data
        }
        
        // Redirect to payment selection page
        if (data.key) {
            // Get payment page URL - you'll need to add this to WordPress admin settings
            const paymentPageUrl = this.getPaymentPageUrl();
            if (paymentPageUrl) {
                // Use buildLanguageAwareUrl to respect Weglot language prefix
                window.location.href = this.buildLanguageAwareUrl(paymentPageUrl);
            } else {
                // Fallback: show success message if no payment page configured
                const orderId = data.id || 'N/A';
                const orderKey = data.key || 'N/A';
                this.showSuccess(this.getTranslatedString('bookingCreatedSuccess', [orderId, orderKey]));
            }
        } else {
            this.showSuccess(this.getTranslatedString('bookingSuccess'));
        }
    }
    
    /**
     * Get payment page URL from WordPress
     */
    getPaymentPageUrl() {
        // This will be provided by WordPress similar to shuttleLandingPageUrl
        return window.shuttlePaymentPageUrl || null;
    }
    
    /**
     * Track GA UA Enhanced Ecommerce: Checkout Step 1
     * Fires when checkout page loads (before order is created)
     */
    trackCheckoutPageLoad() {
        if (!window.ShuttleGAEvents) {
            return;
        }
        
        const gaEvents = new window.ShuttleGAEvents();
        // Get route_id from selected routes if available
        const routeId = this.bookingData.selectedRoutes?.outbound?.routeId || 
                       this.bookingData.selectedRoutes?.outbound?.id || null;
        
        // Call without order_id since order doesn't exist yet
        gaEvents.trackCheckoutStep1(this.bookingData, null, routeId);
    }
    
    /**
     * Handle booking creation error
     */
    handleBookingError(error) {
        const errorMessage = error.message || this.getTranslatedString('bookingError');
        this.showError(errorMessage);
    }
}

// Initialize checkout manager when DOM is ready
(function() {
    if (typeof jQuery !== 'undefined') {
        jQuery(document).ready(function() {
            if (jQuery('#shuttle-checkout-app').length > 0) {
                // Small delay to let Weglot finish initial translation
                setTimeout(function() {
                    window.shuttleCheckoutManager = new ShuttleCheckoutManager();
                    window.shuttleCheckoutManager.init();
                }, 200);
            }
        });
    } else {
        document.addEventListener('DOMContentLoaded', function() {
            if (document.getElementById('shuttle-checkout-app')) {
                // Small delay to let Weglot finish initial translation
                setTimeout(function() {
                    window.shuttleCheckoutManager = new ShuttleCheckoutManager();
                    window.shuttleCheckoutManager.init();
                }, 200);
            }
        });
    }
})();
